import React, { useEffect, useMemo, useState } from 'react'
import { MentorshipPanel } from './admin/MentorshipPanel'
import { ContentLibraryPanel } from './admin/ContentLibraryPanel'
import { useI18n } from '../../i18n/I18nContext'
import { useAccessContext } from '../../context/AccessContext'
import { navigateTo } from '../../services/navigation'
import { mentorAdminService } from '../../services/mentorAdmin'
import type { AiMentor, AiMentorTemplate, AiUsageFilters, AiUsageRow, MentorAdminLlmStatus, MentorAdminOverview, MentorAdminRecord, MentorAdminSkill, MentorAssignment, MentorCoachImpact, MentorLearnerProgression, MentorLearningActivity, MentorLearningPath } from '../../types/mentorAdmin'
import { DetailDrawer, EmptyState, ErrorBanner, EntityActionMenu, FormModal, LoadingState } from './admin/AdminUiPrimitives'

type MentorAdminSection =
  | 'dashboard'
  | 'ai-mentors'
  | 'human-mentors'
  | 'learners'
  | 'skills'
  | 'content-library'
  | 'learning-paths'
  | 'assignments'
  | 'sessions'
  | 'booking-requests'
  | 'reviews'
  | 'rates-fees'
  | 'ledger'
  | 'llm-settings'
  | 'reports'

type AiActionMode = 'lesson' | 'quiz' | 'roleplay' | 'progress_summary' | 'french_reading' | 'french_writing' | 'french_dictee' | 'french_progress'
type RecommendationEvidenceFilter = 'all' | 'ai' | 'activity' | 'coaching'

const sections: Array<{ id: MentorAdminSection; label: string; summary: string }> = [
  { id: 'dashboard', label: 'Dashboard', summary: 'Mentorship product health, next actions, and setup status.' },
  { id: 'ai-mentors', label: 'AI Mentors', summary: 'AI mentor templates, personas, skill tracks, and safe setup state.' },
  { id: 'human-mentors', label: 'Human Mentors', summary: 'Approved human mentors, claims, rates, and teaching readiness.' },
  { id: 'learners', label: 'Apprentices / Learners', summary: 'Learner goals, progression, reviews, and mentor coverage.' },
  { id: 'skills', label: 'Skills', summary: 'Canonical skills and mentorship-specific teaching tracks.' },
  { id: 'content-library', label: 'Content Library', summary: 'Reusable lesson, quiz, activity, and assessment templates shared by AI and human coaches.' },
  { id: 'learning-paths', label: 'Learning Paths', summary: 'Structured upskilling roadmaps and practice milestones.' },
  { id: 'assignments', label: 'Assignments', summary: 'AI mentor and human mentor assignments for learners.' },
  { id: 'sessions', label: 'Sessions', summary: 'Scheduled human sessions and future AI interaction records.' },
  { id: 'booking-requests', label: 'Booking Requests', summary: 'Learner requests for human mentor time.' },
  { id: 'reviews', label: 'Reviews', summary: 'Mentor, learner, and skill progression feedback.' },
  { id: 'rates-fees', label: 'Rates & Fees', summary: 'Human mentor rates and platform fee settings.' },
  { id: 'ledger', label: 'Payments / Ledger', summary: 'Internal off-platform settlement records.' },
  { id: 'llm-settings', label: 'LLM Settings', summary: 'Provider configuration and not-configured status.' },
  { id: 'reports', label: 'Reports', summary: 'Mentorship reports separated from workforce operations.' },
]

const currentMentorshipTab: Partial<Record<MentorAdminSection, string>> = {
  dashboard: 'Dashboard',
  'human-mentors': 'Mentor Approvals',
  learners: 'Learning Goals',
  skills: 'Skills',
  assignments: 'Matches',
  sessions: 'Sessions',
  'booking-requests': 'Bookings',
  reviews: 'Disputes',
  'rates-fees': 'Mentor Rates and Platform Fees',
  ledger: 'Payment Records',
  reports: 'Dashboard',
}

const defaultAiMentorForm = {
  id: '',
  templateId: '',
  name: '',
  slug: '',
  skillTrack: '',
  personaPrompt: '',
  description: '',
  status: 'draft' as AiMentor['status'],
  defaultLanguage: 'en',
  supportedLanguages: 'en',
  difficultyLevels: 'beginner, intermediate, advanced',
  safetyDisclaimer: '',
}

const defaultAssignmentForm = {
  assignmentType: 'ai_mentor' as MentorAssignment['assignmentType'],
  learnerUserId: '',
  learnerProfileId: '',
  aiMentorId: '',
  humanMentorWorkerProfileId: '',
  skillId: '',
  skillTrack: '',
  learningGoalId: '',
  status: 'assigned' as MentorAssignment['status'],
  targetCompletionAt: '',
  progressSummary: '',
}

const defaultSkillForm = {
  name: '',
  category: '',
  description: '',
  status: 'active' as NonNullable<MentorAdminSkill['status']>,
}

const defaultLearningPathForm = {
  skillTrack: '',
  learnerCurrentLevel: 'beginner',
  learnerGoal: '',
  timeline: '30 days',
  learnerUserId: '',
  learnerProfileId: '',
  aiMentorId: '',
  assignmentId: '',
  skillId: '',
  status: 'draft' as MentorLearningPath['status'],
  saveLearningPath: true,
}

const defaultUsageFilters: AiUsageFilters = {
  aiMentorId: '',
  learnerProfileId: '',
  skillTrack: '',
  dateFrom: '',
  dateTo: '',
}

const defaultAiActionForm = {
  skillTrack: '',
  module: '',
  level: 'beginner',
  learnerUserId: '',
  learnerProfileId: '',
  aiMentorId: '',
  assignmentId: '',
  scenario: '',
  learnerMessage: '',
  conversationSummary: '',
  learnerActivity: '',
  quizScore: '',
  completedModules: '',
  languagePreference: 'fr',
  passage: '',
  generatedPassageRequest: 'Generate a short Quebec Secondary 2 French passage.',
  studentParagraph: '',
  assignmentGoal: 'Practice clear Secondary 2 French writing.',
  theme: 'school and daily routines',
  completedExercises: '',
  quizScores: '',
  recurringMistakes: '',
}

const defaultLearningActivityForm = {
  activityType: 'reading_comprehension_result' as MentorLearningActivity['activityType'],
  title: '',
  skillTrack: 'french_school_coach',
  skillId: '',
  assignmentId: '',
  score: '',
  maxScore: '',
  xpEarned: '0',
  masteryPercent: '',
  durationMinutes: '',
}

const defaultCoachingSessionForm = {
  coachWorkerProfileId: '',
  learnerWorkerProfileId: '',
  skillId: '',
  skillTrack: '',
  startedAt: '',
  endedAt: '',
  durationMinutes: '30',
  learnerScoreBefore: '',
  learnerScoreAfter: '',
  aiSummary: '',
  notes: '',
}

const frenchActivityTypeByMode: Partial<Record<AiActionMode, MentorLearningActivity['activityType']>> = {
  french_reading: 'reading_comprehension_result',
  french_writing: 'writing_submission',
  french_dictee: 'dictee_result',
  french_progress: 'admin_parent_note',
}

const csvToArray = (value: string) => value.split(',').map((item) => item.trim()).filter(Boolean)

const fromLocalDatetimeValue = (value: string) => {
  if (!value) return null
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? null : date.toISOString()
}

const firstValue = (row: MentorAdminRecord, keys: string[], fallback = '') => {
  for (const key of keys) {
    const value = row[key]
    if (value !== undefined && value !== null && value !== '') return String(value)
  }
  return fallback
}

const asTextList = (value: unknown): string[] => {
  if (Array.isArray(value)) return value.flatMap((item) => asTextList(item))
  if (value && typeof value === 'object') return Object.values(value as MentorAdminRecord).flatMap((item) => asTextList(item))
  const text = String(value || '').trim()
  return text ? [text] : []
}

const roadmapTextList = (value: unknown): string[] => {
  if (Array.isArray(value)) return value.flatMap((item) => roadmapTextList(item))
  if (value && typeof value === 'object') {
    const row = value as MentorAdminRecord
    const preferred = firstValue(row, ['title', 'name', 'module', 'milestone', 'task', 'label', 'description'])
    return preferred ? [preferred] : Object.values(row).flatMap((item) => roadmapTextList(item))
  }
  const text = String(value || '').trim()
  return text ? [text] : []
}

const learningPathRoadmapPreview = (roadmap: unknown) => {
  if (!roadmap || typeof roadmap !== 'object') return null
  const source = roadmap as MentorAdminRecord
  const modules = roadmapTextList(source.modules).slice(0, 3)
  const milestones = roadmapTextList(source.milestones).slice(0, 3)
  const practiceTasks = roadmapTextList(source.practiceTasks || source.recommendedPracticeTasks || source.practice).slice(0, 3)
  if (!modules.length && !milestones.length && !practiceTasks.length) return null
  return { modules, milestones, practiceTasks }
}

const recommendationAiInsights = (recommendation: { evidenceSummary?: MentorAdminRecord }) => {
  const insights = recommendation.evidenceSummary?.aiStructuredInsights as MentorAdminRecord | undefined
  if (!insights) return null
  const weaknesses = asTextList(insights.weaknesses).slice(0, 3)
  const mistakes = asTextList(insights.mistakes).slice(0, 3)
  const failedQuestions = asTextList(insights.failedQuestions).slice(0, 3)
  const practicePlan = asTextList(insights.practicePlan).slice(0, 3)
  if (!weaknesses.length && !mistakes.length && !failedQuestions.length && !practicePlan.length) return null
  return { weaknesses, mistakes, failedQuestions, practicePlan }
}

const recommendationMatchesEvidenceFilter = (recommendation: { recommendationType?: string; evidenceSummary?: MentorAdminRecord }, filter: RecommendationEvidenceFilter) => {
  if (filter === 'all') return true
  const evidence = recommendation.evidenceSummary || {}
  if (filter === 'ai') return Number(evidence.aiInteractionCount || 0) > 0 || Boolean(evidence.aiPracticeNeed) || Boolean(evidence.aiStructuredInsights)
  if (filter === 'activity') return Number(evidence.activityCount || 0) > 0 || asTextList(evidence.activityTypes).length > 0
  if (filter === 'coaching') return recommendation.recommendationType === 'human_coach' || Number(evidence.coachingMinutesReceived || evidence.coachingMinutes || 0) > 0
  return true
}

const priorityRank: Record<string, number> = { high: 1, medium: 2, low: 3 }

const optionFromRecord = (row: MentorAdminRecord, valueKeys: string[], labelKeys: string[], fallbackLabel: string) => {
  const value = firstValue(row, valueKeys)
  if (!value) return null
  return {
    value,
    label: firstValue(row, labelKeys, fallbackLabel),
  }
}

const uniqueOptions = (options: Array<{ value: string; label: string }>) => {
  const seen = new Set<string>()
  return options.filter((option) => {
    if (seen.has(option.value)) return false
    seen.add(option.value)
    return true
  })
}

const PlaceholderPanel: React.FC<{
  title: string
  description: string
  children?: React.ReactNode
}> = ({ title, description, children }) => (
  <section className="stack mentor-admin-subpanel">
    <div className="admin-panel-toolbar">
      <div className="stack" style={{ gap: '.25rem' }}>
        <h3 style={{ margin: 0 }}>{title}</h3>
        <p className="text-muted">{description}</p>
      </div>
    </div>
    {children}
  </section>
)

export const MentorAdminWorkspace: React.FC = () => {
  const { t, formatCurrency, formatNumber, formatDate } = useI18n()
  const { accessContext } = useAccessContext()
  // Mentor Admin manages tenant-sponsored mentorship (tenant AI mentors,
  // learner assignments, sessions, payments). Platform identities operating at
  // platform scope must pick a tenant first instead of loading tenant-scoped
  // mentorship data against an ambiguous context.
  const platformTenantRequired = Boolean(
    (accessContext?.isSystemSuperAdmin || accessContext?.isPlatformAdmin)
    && !accessContext?.tenantId
  )
  const [activeSection, setActiveSection] = useState<MentorAdminSection>('dashboard')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [overview, setOverview] = useState<MentorAdminOverview | null>(null)
  const [templates, setTemplates] = useState<AiMentorTemplate[]>([])
  const [aiMentors, setAiMentors] = useState<AiMentor[]>([])
  const [humanMentors, setHumanMentors] = useState<MentorAdminRecord[]>([])
  const [pendingHumanMentors, setPendingHumanMentors] = useState<MentorAdminRecord[]>([])
  const [learners, setLearners] = useState<MentorAdminRecord[]>([])
  const [skills, setSkills] = useState<MentorAdminSkill[]>([])
  const [assignments, setAssignments] = useState<MentorAssignment[]>([])
  const [learningPaths, setLearningPaths] = useState<MentorLearningPath[]>([])
  const [usage, setUsage] = useState<AiUsageRow[]>([])
  const [usageFilters, setUsageFilters] = useState<AiUsageFilters>(defaultUsageFilters)
  const [llmStatus, setLlmStatus] = useState<MentorAdminLlmStatus | null>(null)
  const [sessions, setSessions] = useState<MentorAdminRecord[]>([])
  const [bookings, setBookings] = useState<MentorAdminRecord[]>([])
  const [reviews, setReviews] = useState<MentorAdminRecord[]>([])
  const [rates, setRates] = useState<MentorAdminRecord[]>([])
  const [platformFeeSettings, setPlatformFeeSettings] = useState<MentorAdminRecord | null>(null)
  const [payments, setPayments] = useState<MentorAdminRecord[]>([])
  const [busyId, setBusyId] = useState('')
  const [aiMentorFormOpen, setAiMentorFormOpen] = useState(false)
  const [aiMentorForm, setAiMentorForm] = useState(defaultAiMentorForm)
  const [assignmentFormOpen, setAssignmentFormOpen] = useState(false)
  const [assignmentForm, setAssignmentForm] = useState(defaultAssignmentForm)
  const [skillFormOpen, setSkillFormOpen] = useState(false)
  const [skillForm, setSkillForm] = useState(defaultSkillForm)
  const [learningPathFormOpen, setLearningPathFormOpen] = useState(false)
  const [learningPathForm, setLearningPathForm] = useState(defaultLearningPathForm)
  const [learningPathPreview, setLearningPathPreview] = useState<Record<string, any> | string | null>(null)
  const [aiActionFormOpen, setAiActionFormOpen] = useState(false)
  const [aiActionMode, setAiActionMode] = useState<AiActionMode>('lesson')
  const [aiActionForm, setAiActionForm] = useState(defaultAiActionForm)
  const [aiActionPreview, setAiActionPreview] = useState<Record<string, any> | string | null>(null)
  const [aiActionActivitySaved, setAiActionActivitySaved] = useState('')
  const [selectedAiMentor, setSelectedAiMentor] = useState<AiMentor | null>(null)
  const [selectedLearnerProgression, setSelectedLearnerProgression] = useState<MentorLearnerProgression | null>(null)
  const [recommendationEvidenceFilter, setRecommendationEvidenceFilter] = useState<RecommendationEvidenceFilter>('all')
  const [selectedCoachImpact, setSelectedCoachImpact] = useState<MentorCoachImpact | null>(null)
  const [coachingSessionFormOpen, setCoachingSessionFormOpen] = useState(false)
  const [coachingSessionForm, setCoachingSessionForm] = useState(defaultCoachingSessionForm)
  const [learningActivityFormOpen, setLearningActivityFormOpen] = useState(false)
  const [learningActivityForm, setLearningActivityForm] = useState(defaultLearningActivityForm)
  const [formError, setFormError] = useState('')
  const section = sections.find((item) => item.id === activeSection) || sections[0]
  const legacyTabLabel = currentMentorshipTab[activeSection]
  const dataBackedSections = new Set<MentorAdminSection>([
    'human-mentors',
    'learners',
    'skills',
    'assignments',
    'sessions',
    'booking-requests',
    'reviews',
    'rates-fees',
    'ledger',
  ])
  const showCurrentMentorshipData = Boolean(legacyTabLabel && !dataBackedSections.has(activeSection))

  const load = async () => {
    setLoading(true)
    setError('')
    try {
      const optional = async <T,>(request: Promise<{ data: T }>, fallback: T): Promise<T> => {
        try {
          const response = await request
          return response.data
        } catch {
          return fallback
        }
      }
      const overviewRes = await mentorAdminService.getOverview()
      const [
        templateData,
        mentorData,
        humanMentorData,
        learnerData,
        skillData,
        assignmentData,
        learningPathData,
        usageData,
        llmData,
        sessionData,
        bookingData,
        reviewData,
        rateData,
        paymentData,
      ] = await Promise.all([
        optional(mentorAdminService.listAiMentorTemplates(), { success: true, templates: [] }),
        optional(mentorAdminService.listAiMentors(), { success: true, aiMentors: [] }),
        optional(mentorAdminService.listHumanMentors(), { success: true, humanMentors: [], pendingApprovals: [] }),
        optional(mentorAdminService.listLearners(), { success: true, learners: [] }),
        optional(mentorAdminService.listSkills(), { success: true, skills: [], templates: [] }),
        optional(mentorAdminService.listAssignments(), { success: true, assignments: [] }),
        optional(mentorAdminService.listLearningPaths(), { success: true, learningPaths: [] }),
        optional(mentorAdminService.listAiUsage(usageFilters), { success: true, usage: [] }),
        optional<{ success: boolean; llm: MentorAdminLlmStatus | null }>(
          mentorAdminService.getLlmStatus(),
          { success: true, llm: overviewRes.data.overview?.llm || null }
        ),
        optional(mentorAdminService.listSessions(), { success: true, sessions: [] }),
        optional(mentorAdminService.listBookings(), { success: true, bookings: [] }),
        optional(mentorAdminService.listReviews(), { success: true, reviews: [] }),
        optional(mentorAdminService.listRates(), { success: true, rates: [], platformFeeSettings: null }),
        optional(mentorAdminService.listPayments(), { success: true, paymentRecords: [] }),
      ])
      setOverview(overviewRes.data.overview)
      setTemplates(templateData.templates || skillData.templates || [])
      setAiMentors(mentorData.aiMentors || [])
      setHumanMentors(humanMentorData.humanMentors || [])
      setPendingHumanMentors(humanMentorData.pendingApprovals || [])
      setLearners(learnerData.learners || [])
      setSkills(skillData.skills || [])
      setAssignments(assignmentData.assignments || [])
      setLearningPaths(learningPathData.learningPaths || [])
      setUsage(usageData.usage || [])
      setLlmStatus(llmData.llm || overviewRes.data.overview?.llm || null)
      setSessions(sessionData.sessions || [])
      setBookings(bookingData.bookings || [])
      setReviews(reviewData.reviews || [])
      setRates(rateData.rates || [])
      setPlatformFeeSettings(rateData.platformFeeSettings || null)
      setPayments(paymentData.paymentRecords || [])
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || t('Unable to load Mentor Admin data.'))
    } finally {
      setLoading(false)
    }
  }

  const loadUsage = async (filters: AiUsageFilters = usageFilters) => {
    setBusyId('ai-usage-report')
    setError('')
    try {
      const response = await mentorAdminService.listAiUsage(filters)
      setUsage(response.data.usage || [])
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || t('Unable to load AI usage report.'))
    } finally {
      setBusyId('')
    }
  }

  useEffect(() => {
    if (platformTenantRequired) return
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [platformTenantRequired])

  const createFromTemplate = async (template: AiMentorTemplate) => {
    setBusyId(template.id)
    try {
      await mentorAdminService.createAiMentor({
        templateId: template.id,
        name: template.name,
        slug: template.slug,
        skillTrack: template.skillTrack,
        personaPrompt: template.personaPrompt,
        description: template.description,
        status: 'draft',
        defaultLanguage: template.defaultLanguage,
        supportedLanguages: template.supportedLanguages,
        difficultyLevels: template.difficultyLevels,
        safetyDisclaimer: template.safetyDisclaimer,
      })
      await load()
    } finally {
      setBusyId('')
    }
  }

  const openNewAiMentorForm = (template?: AiMentorTemplate) => {
    setFormError('')
    setAiMentorForm({
      ...defaultAiMentorForm,
      templateId: template?.id || '',
      name: template?.name || '',
      slug: template?.slug || '',
      skillTrack: template?.skillTrack || '',
      personaPrompt: template?.personaPrompt || '',
      description: template?.description || '',
      defaultLanguage: template?.defaultLanguage || 'en',
      supportedLanguages: (template?.supportedLanguages || ['en']).join(', '),
      difficultyLevels: (template?.difficultyLevels || ['beginner', 'intermediate', 'advanced']).join(', '),
      safetyDisclaimer: template?.safetyDisclaimer || '',
    })
    setAiMentorFormOpen(true)
  }

  const openEditAiMentorForm = (mentor: AiMentor) => {
    setFormError('')
    setAiMentorForm({
      id: mentor.id,
      templateId: mentor.templateId || '',
      name: mentor.name,
      slug: mentor.slug,
      skillTrack: mentor.skillTrack,
      personaPrompt: mentor.personaPrompt,
      description: mentor.description || '',
      status: mentor.status,
      defaultLanguage: mentor.defaultLanguage || 'en',
      supportedLanguages: (mentor.supportedLanguages || ['en']).join(', '),
      difficultyLevels: (mentor.difficultyLevels || ['beginner', 'intermediate', 'advanced']).join(', '),
      safetyDisclaimer: mentor.safetyDisclaimer || '',
    })
    setAiMentorFormOpen(true)
  }

  const applyTemplateToAiMentorForm = (templateId: string) => {
    const template = templates.find((item) => item.id === templateId)
    setAiMentorForm((current) => ({
      ...current,
      templateId,
      name: template?.name || current.name,
      slug: template?.slug || current.slug,
      skillTrack: template?.skillTrack || current.skillTrack,
      personaPrompt: template?.personaPrompt || current.personaPrompt,
      description: template?.description || current.description,
      defaultLanguage: template?.defaultLanguage || current.defaultLanguage,
      supportedLanguages: template?.supportedLanguages?.join(', ') || current.supportedLanguages,
      difficultyLevels: template?.difficultyLevels?.join(', ') || current.difficultyLevels,
      safetyDisclaimer: template?.safetyDisclaimer || current.safetyDisclaimer,
    }))
  }

  const saveAiMentorForm = async () => {
    setFormError('')
    const payload = {
      templateId: aiMentorForm.templateId || null,
      name: aiMentorForm.name.trim(),
      slug: aiMentorForm.slug.trim(),
      skillTrack: aiMentorForm.skillTrack.trim(),
      personaPrompt: aiMentorForm.personaPrompt.trim(),
      description: aiMentorForm.description.trim() || null,
      status: aiMentorForm.status,
      defaultLanguage: aiMentorForm.defaultLanguage.trim() || 'en',
      supportedLanguages: csvToArray(aiMentorForm.supportedLanguages),
      difficultyLevels: csvToArray(aiMentorForm.difficultyLevels),
      safetyDisclaimer: aiMentorForm.safetyDisclaimer.trim(),
    }
    if (!payload.name || !payload.skillTrack || !payload.personaPrompt || !payload.safetyDisclaimer) {
      setFormError(t('Name, skill track, persona prompt, and safety disclaimer are required.'))
      return
    }
    setBusyId(aiMentorForm.id || 'ai-mentor-form')
    try {
      if (aiMentorForm.id) {
        await mentorAdminService.updateAiMentor(aiMentorForm.id, payload)
      } else {
        await mentorAdminService.createAiMentor(payload)
      }
      setAiMentorFormOpen(false)
      setAiMentorForm(defaultAiMentorForm)
      await load()
    } catch (err: any) {
      setFormError(err?.response?.data?.message || err?.message || t('Unable to save AI mentor.'))
    } finally {
      setBusyId('')
    }
  }

  const updateAiMentorStatus = async (mentor: AiMentor, status: AiMentor['status']) => {
    setBusyId(mentor.id)
    try {
      await mentorAdminService.updateAiMentor(mentor.id, { status })
      await load()
    } finally {
      setBusyId('')
    }
  }

  const decideHumanMentorClaim = async (claim: MentorAdminRecord, action: 'approve' | 'reject' | 'suspend') => {
    const claimId = firstValue(claim, ['id', 'claimId'])
    if (!claimId) return
    setBusyId(claimId)
    try {
      await mentorAdminService.decideHumanMentorClaim(claimId, action)
      await load()
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || t('Unable to update mentor claim.'))
    } finally {
      setBusyId('')
    }
  }

  const saveAssignmentForm = async () => {
    setFormError('')
    const targetCompletionAt = fromLocalDatetimeValue(assignmentForm.targetCompletionAt)
    const payload = {
      assignmentType: assignmentForm.assignmentType,
      learnerUserId: assignmentForm.learnerUserId.trim() || null,
      learnerProfileId: assignmentForm.learnerProfileId.trim() || null,
      aiMentorId: assignmentForm.assignmentType === 'ai_mentor' ? assignmentForm.aiMentorId || null : null,
      humanMentorWorkerProfileId: assignmentForm.assignmentType === 'human_mentor' ? assignmentForm.humanMentorWorkerProfileId.trim() || null : null,
      skillId: assignmentForm.skillId.trim() || null,
      skillTrack: assignmentForm.skillTrack.trim() || null,
      learningGoalId: assignmentForm.learningGoalId.trim() || null,
      status: assignmentForm.status,
      targetCompletionAt,
      progressSummary: assignmentForm.progressSummary.trim() || null,
    }
    if (!payload.learnerUserId && !payload.learnerProfileId) {
      setFormError(t('Add a learner user id or learner profile id.'))
      return
    }
    if (payload.assignmentType === 'ai_mentor' && !payload.aiMentorId) {
      setFormError(t('Choose an AI mentor for AI assignments.'))
      return
    }
    if (payload.assignmentType === 'human_mentor' && !payload.humanMentorWorkerProfileId) {
      setFormError(t('Add a human mentor worker profile id.'))
      return
    }
    setBusyId('assignment-form')
    try {
      await mentorAdminService.createAssignment(payload)
      setAssignmentForm(defaultAssignmentForm)
      setAssignmentFormOpen(false)
      await load()
    } catch (err: any) {
      setFormError(err?.response?.data?.message || err?.message || t('Unable to create assignment.'))
    } finally {
      setBusyId('')
    }
  }

  const updateAssignmentStatus = async (assignment: MentorAssignment, status: MentorAssignment['status']) => {
    setBusyId(assignment.id)
    try {
      await mentorAdminService.updateAssignment(assignment.id, { status })
      await load()
    } finally {
      setBusyId('')
    }
  }

  const saveSkillForm = async () => {
    setFormError('')
    const payload = {
      name: skillForm.name.trim(),
      category: skillForm.category.trim() || null,
      description: skillForm.description.trim() || null,
      status: skillForm.status,
    }
    if (!payload.name) {
      setFormError(t('Skill name is required.'))
      return
    }
    setBusyId('skill-form')
    try {
      await mentorAdminService.createSkill(payload)
      setSkillForm(defaultSkillForm)
      setSkillFormOpen(false)
      await load()
    } catch (err: any) {
      setFormError(err?.response?.data?.message || err?.message || t('Unable to create skill.'))
    } finally {
      setBusyId('')
    }
  }

  const updateSkillStatus = async (skill: MentorAdminSkill, status: NonNullable<MentorAdminSkill['status']>) => {
    setBusyId(skill.id)
    try {
      await mentorAdminService.updateSkill(skill.id, { status })
      await load()
    } finally {
      setBusyId('')
    }
  }

  const openLearningPathForm = () => {
    setFormError('')
    setLearningPathPreview(null)
    setLearningPathForm({
      ...defaultLearningPathForm,
      aiMentorId: aiMentors.find((mentor) => mentor.status === 'active')?.id || '',
      skillTrack: aiMentors.find((mentor) => mentor.status === 'active')?.skillTrack || activeTemplates[0]?.skillTrack || '',
    })
    setLearningPathFormOpen(true)
  }

  const openAiActionForm = (mode: AiActionMode) => {
    const activeMentor = aiMentors.find((mentor) => mentor.status === 'active')
    setFormError('')
    setAiActionPreview(null)
    setAiActionActivitySaved('')
    setAiActionMode(mode)
    setAiActionForm({
      ...defaultAiActionForm,
      aiMentorId: activeMentor?.id || '',
      skillTrack: activeMentor?.skillTrack || activeTemplates[0]?.skillTrack || '',
      module: mode === 'progress_summary' ? '' : 'Starter module',
      level: mode.startsWith('french_') ? 'secondary_2' : defaultAiActionForm.level,
      scenario: mode === 'roleplay' ? 'Learner practices a realistic workplace conversation.' : '',
      learnerMessage: mode === 'roleplay' ? 'I would like to practice and get feedback.' : '',
      learnerActivity: mode === 'progress_summary'
        ? 'Learner completed recent practice tasks and mentor checkpoints.'
        : mode === 'french_progress'
          ? 'Learner practiced reading comprehension, writing correction, and dictée exercises this week.'
          : '',
      languagePreference: 'fr',
      passage: '',
      generatedPassageRequest: mode === 'french_reading' ? 'Generate a short Quebec Secondary 2 French passage.' : '',
      studentParagraph: mode === 'french_writing' ? 'Je suis aller au parc avec mes amis et on a jouer au soccer.' : '',
      assignmentGoal: 'Practice clear Secondary 2 French writing.',
      theme: mode === 'french_dictee' ? 'school and daily routines' : '',
      completedExercises: '',
      quizScores: '',
      recurringMistakes: '',
    })
    setAiActionFormOpen(true)
  }

  const learnerIdFromRecord = (learner: MentorAdminRecord) => firstValue(learner, ['learnerProfileId', 'workerProfileId', 'worker_profile_id', 'profileId', 'learnerUserId', 'userId'])

  const viewLearnerProgression = async (learner: MentorAdminRecord) => {
    const learnerId = learnerIdFromRecord(learner)
    if (!learnerId) return
    setBusyId(learnerId)
    setError('')
    try {
      const response = await mentorAdminService.getLearnerProgression(learnerId)
      setRecommendationEvidenceFilter('all')
      setSelectedLearnerProgression(response.data)
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || t('Unable to load learner progression.'))
    } finally {
      setBusyId('')
    }
  }

  const generateLearnerRecommendations = async (learner: MentorAdminRecord) => {
    const learnerId = learnerIdFromRecord(learner)
    if (!learnerId) return
    setBusyId(`recommend-${learnerId}`)
    setError('')
    try {
      const response = await mentorAdminService.generateLearnerRecommendations(learnerId)
      setSelectedLearnerProgression(response.data)
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || t('Unable to generate learner recommendations.'))
    } finally {
      setBusyId('')
    }
  }

  const saveLearningActivity = async () => {
    if (!selectedLearnerProgression?.learner) return
    const learnerProfileId = firstValue(selectedLearnerProgression.learner, ['learnerProfileId', 'workerProfileId', 'profileId'])
    const learnerUserId = firstValue(selectedLearnerProgression.learner, ['learnerUserId', 'userId'])
    if (!learnerProfileId && !learnerUserId) {
      setFormError(t('Learner profile or user account is required.'))
      return
    }
    const score = learningActivityForm.score.trim() ? Number(learningActivityForm.score) : null
    const maxScore = learningActivityForm.maxScore.trim() ? Number(learningActivityForm.maxScore) : null
    const xpEarned = learningActivityForm.xpEarned.trim() ? Number(learningActivityForm.xpEarned) : 0
    const masteryPercent = learningActivityForm.masteryPercent.trim() ? Number(learningActivityForm.masteryPercent) : null
    const durationMinutes = learningActivityForm.durationMinutes.trim() ? Number(learningActivityForm.durationMinutes) : null
    if ([score, maxScore, xpEarned, masteryPercent, durationMinutes].some((value) => value !== null && !Number.isFinite(value))) {
      setFormError(t('Scores, XP, mastery, and duration must be valid numbers.'))
      return
    }
    setBusyId('learning-activity-form')
    setFormError('')
    try {
      await mentorAdminService.createLearningActivity({
        learnerProfileId: learnerProfileId || null,
        learnerUserId: learnerUserId || null,
        activityType: learningActivityForm.activityType,
        title: learningActivityForm.title.trim() || null,
        skillTrack: learningActivityForm.skillTrack.trim() || null,
        skillId: learningActivityForm.skillId.trim() || null,
        assignmentId: learningActivityForm.assignmentId.trim() || null,
        score,
        maxScore,
        xpEarned,
        masteryPercent,
        durationMinutes,
      })
      const learnerId = learnerProfileId || learnerUserId
      const response = await mentorAdminService.getLearnerProgression(learnerId)
      setSelectedLearnerProgression(response.data)
      setLearningActivityForm(defaultLearningActivityForm)
      setLearningActivityFormOpen(false)
    } catch (err: any) {
      setFormError(err?.response?.data?.message || err?.message || t('Unable to record learning activity.'))
    } finally {
      setBusyId('')
    }
  }

  const viewCoachImpact = async (mentor: MentorAdminRecord) => {
    const coachId = firstValue(mentor, ['mentorWorkerProfileId', 'workerProfileId', 'worker_profile_id', 'mentor_profile_id'])
    if (!coachId) return
    setBusyId(coachId)
    setError('')
    try {
      const response = await mentorAdminService.getCoachImpact(coachId)
      setSelectedCoachImpact(response.data)
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || t('Unable to load coach impact.'))
    } finally {
      setBusyId('')
    }
  }

  const openCoachingSessionForm = (mentor?: MentorAdminRecord) => {
    setFormError('')
    const coachWorkerProfileId = mentor ? firstValue(mentor, ['mentorWorkerProfileId', 'workerProfileId', 'worker_profile_id', 'mentor_profile_id']) : ''
    setCoachingSessionForm({
      ...defaultCoachingSessionForm,
      coachWorkerProfileId,
      skillId: mentor ? firstValue(mentor, ['skillId', 'canonicalSkillId', 'canonical_skill_id']) : '',
      skillTrack: mentor ? firstValue(mentor, ['skillName', 'canonicalSkillName', 'skillTrack']) : '',
    })
    setCoachingSessionFormOpen(true)
  }

  const saveCoachingSessionForm = async () => {
    setFormError('')
    const durationMinutes = Number(coachingSessionForm.durationMinutes)
    const learnerScoreBefore = coachingSessionForm.learnerScoreBefore.trim() ? Number(coachingSessionForm.learnerScoreBefore) : null
    const learnerScoreAfter = coachingSessionForm.learnerScoreAfter.trim() ? Number(coachingSessionForm.learnerScoreAfter) : null
    if (!coachingSessionForm.coachWorkerProfileId || !coachingSessionForm.learnerWorkerProfileId) {
      setFormError(t('Choose both a coach and a learner.'))
      return
    }
    if (!Number.isFinite(durationMinutes) || durationMinutes < 0 || !Number.isInteger(durationMinutes)) {
      setFormError(t('Duration minutes must be a whole number.'))
      return
    }
    if ([learnerScoreBefore, learnerScoreAfter].some((value) => value !== null && !Number.isFinite(value))) {
      setFormError(t('Before and after scores must be valid numbers.'))
      return
    }
    setBusyId('coaching-session-form')
    try {
      await mentorAdminService.createCoachingSession({
        coachWorkerProfileId: coachingSessionForm.coachWorkerProfileId,
        learnerWorkerProfileId: coachingSessionForm.learnerWorkerProfileId,
        skillId: coachingSessionForm.skillId.trim() || null,
        skillTrack: coachingSessionForm.skillTrack.trim() || null,
        startedAt: fromLocalDatetimeValue(coachingSessionForm.startedAt),
        endedAt: fromLocalDatetimeValue(coachingSessionForm.endedAt),
        durationMinutes,
        learnerScoreBefore,
        learnerScoreAfter,
        aiSummary: coachingSessionForm.aiSummary.trim() || null,
        notes: coachingSessionForm.notes.trim() || null,
      })
      setCoachingSessionForm(defaultCoachingSessionForm)
      setCoachingSessionFormOpen(false)
      await load()
    } catch (err: any) {
      setFormError(err?.response?.data?.message || err?.message || t('Unable to record coaching session.'))
    } finally {
      setBusyId('')
    }
  }

  const generateLearningPath = async () => {
    setFormError('')
    setLearningPathPreview(null)
    const payload = {
      skillTrack: learningPathForm.skillTrack.trim(),
      learnerCurrentLevel: learningPathForm.learnerCurrentLevel.trim(),
      learnerGoal: learningPathForm.learnerGoal.trim(),
      timeline: learningPathForm.timeline.trim(),
      learnerUserId: learningPathForm.learnerUserId.trim() || null,
      learnerProfileId: learningPathForm.learnerProfileId.trim() || null,
      aiMentorId: learningPathForm.aiMentorId || null,
      assignmentId: learningPathForm.assignmentId || null,
      skillId: learningPathForm.skillId || null,
      status: learningPathForm.status,
      saveLearningPath: learningPathForm.saveLearningPath,
    }
    if (!payload.skillTrack || !payload.learnerCurrentLevel || !payload.learnerGoal || !payload.timeline) {
      setFormError(t('Skill track, learner level, learner goal, and timeline are required.'))
      return
    }
    setBusyId('learning-path-form')
    try {
      const response = await mentorAdminService.generateLearningPath(payload)
      if (!response.data.success) {
        setFormError(response.data.message || t('LLM setup required before learning paths can be generated.'))
        return
      }
      setLearningPathPreview(response.data.result || response.data.learningPath?.roadmap || null)
      if (response.data.learningPath) {
        setLearningPathFormOpen(false)
        setLearningPathForm(defaultLearningPathForm)
        await load()
      }
    } catch (err: any) {
      setFormError(err?.response?.data?.message || err?.message || t('Unable to generate learning path.'))
    } finally {
      setBusyId('')
    }
  }

  const applyAssignmentToAiAction = (assignmentId: string) => {
    const assignment = assignmentOptions.find((option) => option.value === assignmentId)
    setAiActionForm({
      ...aiActionForm,
      assignmentId,
      aiMentorId: assignment?.aiMentorId || aiActionForm.aiMentorId,
      learnerUserId: assignment?.learnerUserId || aiActionForm.learnerUserId,
      learnerProfileId: assignment?.learnerProfileId || aiActionForm.learnerProfileId,
      skillTrack: assignment?.skillTrack || aiActionForm.skillTrack,
    })
  }

  const runAiAction = async () => {
    setFormError('')
    setAiActionPreview(null)
    setAiActionActivitySaved('')
    const context = {
      learnerUserId: aiActionForm.learnerUserId.trim() || null,
      learnerProfileId: aiActionForm.learnerProfileId.trim() || null,
      aiMentorId: aiActionForm.aiMentorId || null,
      assignmentId: aiActionForm.assignmentId || null,
    }
    setBusyId('ai-action-form')
    try {
      let response
      if (aiActionMode === 'lesson' || aiActionMode === 'quiz') {
        const payload = {
          ...context,
          skillTrack: aiActionForm.skillTrack.trim(),
          module: aiActionForm.module.trim(),
          level: aiActionForm.level.trim(),
        }
        if (!payload.skillTrack || !payload.module || !payload.level) {
          setFormError(t('Skill track, module, and level are required.'))
          return
        }
        response = aiActionMode === 'lesson'
          ? await mentorAdminService.generateLesson(payload)
          : await mentorAdminService.generateQuiz(payload)
      } else if (aiActionMode === 'roleplay') {
        const payload = {
          ...context,
          skillTrack: aiActionForm.skillTrack.trim(),
          scenario: aiActionForm.scenario.trim(),
          learnerMessage: aiActionForm.learnerMessage.trim(),
          conversationSummary: aiActionForm.conversationSummary.trim() || null,
        }
        if (!payload.skillTrack || !payload.scenario || !payload.learnerMessage) {
          setFormError(t('Skill track, scenario, and learner message are required.'))
          return
        }
        response = await mentorAdminService.runRoleplay(payload)
      } else if (aiActionMode === 'progress_summary') {
        const payload = {
          ...context,
          learnerActivity: aiActionForm.learnerActivity.trim(),
          quizScore: aiActionForm.quizScore.trim() || null,
          completedModules: csvToArray(aiActionForm.completedModules),
        }
        if (!payload.learnerActivity) {
          setFormError(t('Learner activity is required.'))
          return
        }
        response = await mentorAdminService.generateProgressSummary(payload)
      } else if (aiActionMode === 'french_reading') {
        response = await mentorAdminService.frenchReadingComprehension({
          ...context,
          learnerLevel: aiActionForm.level.trim(),
          passage: aiActionForm.passage.trim() || null,
          generatedPassageRequest: aiActionForm.generatedPassageRequest.trim() || null,
          languagePreference: aiActionForm.languagePreference as 'en' | 'fr',
        })
      } else if (aiActionMode === 'french_writing') {
        if (!aiActionForm.studentParagraph.trim()) {
          setFormError(t('Student paragraph is required.'))
          return
        }
        response = await mentorAdminService.frenchWritingCorrection({
          ...context,
          studentParagraph: aiActionForm.studentParagraph.trim(),
          assignmentGoal: aiActionForm.assignmentGoal.trim(),
          learnerLevel: aiActionForm.level.trim(),
          languagePreference: aiActionForm.languagePreference as 'en' | 'fr',
        })
      } else if (aiActionMode === 'french_dictee') {
        response = await mentorAdminService.frenchDictee({
          ...context,
          learnerLevel: aiActionForm.level.trim(),
          theme: aiActionForm.theme.trim(),
          languagePreference: aiActionForm.languagePreference as 'en' | 'fr',
        })
      } else {
        response = await mentorAdminService.frenchProgressSummary({
          ...context,
          learnerActivity: aiActionForm.learnerActivity.trim(),
          completedExercises: csvToArray(aiActionForm.completedExercises),
          quizScores: csvToArray(aiActionForm.quizScores),
          recurringMistakes: csvToArray(aiActionForm.recurringMistakes),
          languagePreference: aiActionForm.languagePreference as 'en' | 'fr',
        })
      }
      if (!response.data.success) {
        setFormError(response.data.message || t('LLM setup required before AI mentor actions can run.'))
        return
      }
      setAiActionPreview(response.data.result || null)
      await load()
    } catch (err: any) {
      setFormError(err?.response?.data?.message || err?.message || t('Unable to run AI mentor action.'))
    } finally {
      setBusyId('')
    }
  }

  const saveAiActionPreviewAsActivity = async () => {
    if (!aiActionPreview) return
    const activityType = frenchActivityTypeByMode[aiActionMode]
    if (!activityType) return
    const learnerProfileId = aiActionForm.learnerProfileId.trim()
    const learnerUserId = aiActionForm.learnerUserId.trim()
    if (!learnerProfileId && !learnerUserId) {
      setFormError(t('Choose a learner before saving AI output as activity evidence.'))
      return
    }
    setBusyId('ai-action-activity')
    setFormError('')
    try {
      const response = await mentorAdminService.createLearningActivity({
        learnerProfileId: learnerProfileId || null,
        learnerUserId: learnerUserId || null,
        assignmentId: aiActionForm.assignmentId || null,
        skillTrack: 'french_school_coach',
        activityType,
        title: aiActionTitle,
        xpEarned: activityType === 'admin_parent_note' ? 0 : 10,
        durationMinutes: activityType === 'admin_parent_note' ? null : 15,
        metadata: {
          aiActionMode,
          aiResult: aiActionPreview,
          learnerLevel: aiActionForm.level,
          languagePreference: aiActionForm.languagePreference,
        },
      })
      setAiActionActivitySaved(response.data.activity.id || t('Saved'))
      const learnerId = learnerProfileId || learnerUserId
      if (selectedLearnerProgression?.learner) {
        const selectedLearnerId = firstValue(selectedLearnerProgression.learner, ['learnerProfileId', 'workerProfileId', 'profileId', 'learnerUserId', 'userId'])
        if (selectedLearnerId === learnerId) {
          const progression = await mentorAdminService.getLearnerProgression(learnerId)
          setSelectedLearnerProgression(progression.data)
        }
      }
    } catch (err: any) {
      setFormError(err?.response?.data?.message || err?.message || t('Unable to save AI output as activity evidence.'))
    } finally {
      setBusyId('')
    }
  }

  const updateLearningPathStatus = async (learningPath: MentorLearningPath, status: MentorLearningPath['status']) => {
    setBusyId(learningPath.id)
    try {
      await mentorAdminService.updateLearningPath(learningPath.id, { status })
      await load()
    } finally {
      setBusyId('')
    }
  }

  const dashboardMetrics = useMemo(() => {
    const data = overview || {
      activeAiMentors: 0,
      activeHumanMentors: 0,
      activeLearners: 0,
      activeAssignments: 0,
      learningGoals: 0,
      sessionsCompleted: 0,
      pendingMentorApprovals: 0,
      averageMentorRating: 0,
      aiInteractionsThisMonth: 0,
      tokenUsageThisMonth: 0,
      estimatedAiCostThisMonth: 0,
    }
    return [
      ['Active AI mentors', formatNumber(data.activeAiMentors || 0)],
      ['Active human mentors', formatNumber(data.activeHumanMentors || 0)],
      ['Active learners', formatNumber(data.activeLearners || 0)],
      ['Active assignments', formatNumber(data.activeAssignments || 0)],
      ['Learning goals', formatNumber(data.learningGoals || 0)],
      ['Sessions completed', formatNumber(data.sessionsCompleted || 0)],
      ['Pending mentor approvals', formatNumber(data.pendingMentorApprovals || 0)],
      ['Average mentor rating', Number(data.averageMentorRating || 0).toFixed(1)],
      ['AI interactions this month', formatNumber(data.aiInteractionsThisMonth || 0)],
      ['Token usage this month', formatNumber(data.tokenUsageThisMonth || 0)],
      ['Estimated AI cost this month', formatCurrency(data.estimatedAiCostThisMonth || 0, 'USD')],
    ]
  }, [formatCurrency, formatNumber, overview])

  const activeTemplates = useMemo(() => templates.filter((template) => template.status === 'active'), [templates])

  const learnerOptions = useMemo(() => uniqueOptions(learners
    .map((learner) => optionFromRecord(
      learner,
      ['learnerUserId', 'userId', 'workerUserId', 'worker_user_id'],
      ['workerName', 'learnerName', 'fullName', 'name', 'workerProfileId', 'worker_profile_id'],
      t('Learner')
    ))
    .filter(Boolean) as Array<{ value: string; label: string }>), [learners, t])

  const learnerProfileOptions = useMemo(() => uniqueOptions(learners
    .map((learner) => optionFromRecord(
      learner,
      ['learnerProfileId', 'workerProfileId', 'worker_profile_id', 'profileId'],
      ['workerName', 'learnerName', 'fullName', 'name', 'workerProfileId', 'worker_profile_id'],
      t('Learner profile')
    ))
    .filter(Boolean) as Array<{ value: string; label: string }>), [learners, t])

  const learningGoalOptions = useMemo(() => learners
    .map((learner) => {
      const value = firstValue(learner, ['id', 'learningGoalId'])
      const learnerProfileId = firstValue(learner, ['learnerProfileId', 'workerProfileId', 'worker_profile_id', 'profileId'])
      const learnerUserId = firstValue(learner, ['learnerUserId', 'userId', 'workerUserId', 'worker_user_id'])
      if (!value) return null
      return {
        value,
        learnerProfileId,
        learnerUserId,
        skillId: firstValue(learner, ['skillId', 'canonicalSkillId', 'canonical_skill_id']),
        skillName: firstValue(learner, ['skillName', 'canonicalSkillName', 'name'], t('Skill')),
        label: `${firstValue(learner, ['workerName', 'learnerName', 'fullName', 'name', 'workerProfileId', 'worker_profile_id'], t('Learner'))} - ${firstValue(learner, ['skillName', 'canonicalSkillName', 'name'], t('Learning goal'))}`,
      }
    })
    .filter((option): option is { value: string; learnerProfileId: string; learnerUserId: string; skillId: string; skillName: string; label: string } => Boolean(option))
    .filter((option) => (
      (!assignmentForm.learnerProfileId || option.learnerProfileId === assignmentForm.learnerProfileId)
      && (!assignmentForm.learnerUserId || !option.learnerUserId || option.learnerUserId === assignmentForm.learnerUserId)
    )), [assignmentForm.learnerProfileId, assignmentForm.learnerUserId, learners, t])

  const humanMentorOptions = useMemo(() => uniqueOptions(humanMentors
    .map((mentor) => optionFromRecord(
      mentor,
      ['mentorWorkerProfileId', 'workerProfileId', 'worker_profile_id', 'mentor_profile_id'],
      ['workerName', 'mentorName', 'fullName', 'name', 'workerProfileId', 'worker_profile_id'],
      t('Human mentor')
    ))
    .filter(Boolean) as Array<{ value: string; label: string }>), [humanMentors, t])

  const assignmentOptions = useMemo(() => assignments.map((assignment) => ({
    value: assignment.id,
    label: `${assignment.learnerName || assignment.learnerProfileId || assignment.learnerUserId || t('Learner')} - ${assignment.assignmentType === 'ai_mentor' ? assignment.aiMentorName || t('AI mentor') : assignment.humanMentorName || t('Human mentor')}`,
    skillTrack: assignment.skillTrack || '',
    aiMentorId: assignment.aiMentorId || '',
    learnerUserId: assignment.learnerUserId || '',
    learnerProfileId: assignment.learnerProfileId || '',
  })), [assignments, t])

  const skillTrackOptions = useMemo(() => uniqueOptions([
    ...aiMentors.map((mentor) => ({ value: mentor.skillTrack, label: mentor.name ? `${mentor.name} - ${mentor.skillTrack}` : mentor.skillTrack })),
    ...activeTemplates.map((template) => ({ value: template.skillTrack, label: template.name ? `${template.name} - ${template.skillTrack}` : template.skillTrack })),
    ...usage.map((row) => ({ value: row.skillTrack || '', label: row.skillTrack || '' })),
  ].filter((option) => option.value)), [activeTemplates, aiMentors, usage])

  const selectedAiMentorAssignments = useMemo(() => (
    selectedAiMentor
      ? assignments.filter((assignment) => assignment.aiMentorId === selectedAiMentor.id)
      : []
  ), [assignments, selectedAiMentor])

  const selectedAiMentorUsage = useMemo(() => (
    selectedAiMentor
      ? usage.filter((row) => row.aiMentorId === selectedAiMentor.id).slice(0, 8)
      : []
  ), [selectedAiMentor, usage])

  const filteredLearnerRecommendations = useMemo(() => (
    [...(selectedLearnerProgression?.recommendations || [])]
      .filter((recommendation) => recommendationMatchesEvidenceFilter(recommendation, recommendationEvidenceFilter))
      .sort((a, b) => {
        const priorityDelta = (priorityRank[a.priority] || 4) - (priorityRank[b.priority] || 4)
        if (priorityDelta !== 0) return priorityDelta
        return Number(b.confidence || 0) - Number(a.confidence || 0)
      })
  ), [selectedLearnerProgression, recommendationEvidenceFilter])

  const learnerCurrentPlan = useMemo(() => {
    const paths = selectedLearnerProgression?.learningPaths || []
    const assignments = selectedLearnerProgression?.assignments || []
    const activePath = paths.find((path) => path.status === 'active') || paths.find((path) => path.status !== 'archived') || null
    const activeAiAssignment = assignments.find((assignment) => (
      assignment.assignmentType === 'ai_mentor'
      && ['active', 'assigned'].includes(String(assignment.status || 'assigned'))
    )) || null
    const activeHumanAssignment = assignments.find((assignment) => (
      assignment.assignmentType === 'human_mentor'
      && ['active', 'assigned'].includes(String(assignment.status || 'assigned'))
    )) || null
    return {
      activePath,
      activeAiAssignment,
      activeHumanAssignment,
      topRecommendation: filteredLearnerRecommendations[0] || selectedLearnerProgression?.recommendations?.[0] || null,
    }
  }, [filteredLearnerRecommendations, selectedLearnerProgression])

  const aiActionTitle = {
    lesson: t('Generate lesson'),
    quiz: t('Generate quiz'),
    roleplay: t('Roleplay practice'),
    progress_summary: t('Progress summary'),
    french_reading: t('French reading comprehension'),
    french_writing: t('French writing correction'),
    french_dictee: t('French dictée practice'),
    french_progress: t('French progress summary'),
  }[aiActionMode]

  if (platformTenantRequired) {
    return (
      <section className="mentor-admin-workspace stack" data-testid="mentor-admin-workspace">
        <div className="panel workspace-panel stack" data-testid="mentor-admin-tenant-required">
          <h2 className="heading-lg" style={{ margin: 0 }}>{t('Mentor Admin')}</h2>
          <p className="text-muted">
            {t('Mentor Admin manages tenant-sponsored mentorship and learning operations. Select a tenant to manage tenant-owned data.')}
          </p>
          <div className="button-row">
            <button type="button" className="btn btn-primary" onClick={() => navigateTo('/admin')}>
              {t('Select tenant')}
            </button>
            <button type="button" className="btn btn-secondary" onClick={() => navigateTo('/platform')}>
              {t('Go to Platform Console')}
            </button>
          </div>
          <p className="helper">
            {t('Global mentor templates and platform learning defaults live in the Platform Console under Learning & Mentor Catalog.')}
          </p>
        </div>
      </section>
    )
  }

  return (
    <section className="mentor-admin-workspace stack" data-testid="mentor-admin-workspace">
      <div className="panel workspace-panel stack mentor-admin-hero">
        <div className="admin-panel-toolbar">
          <div className="stack" style={{ gap: '.35rem' }}>
            <span className="status-pill status-pill--approved">{t('Standalone product surface')}</span>
            <h2 className="heading-lg" style={{ margin: 0 }}>{t('Mentor Admin')}</h2>
            <p className="text-muted">
              {t('AI + Human Skill Mentorship is managed here as its own workspace, separate from company and agency workforce operations.')}
            </p>
          </div>
          <div className="button-row">
            <span className="status-pill">{t('Isolated access')}</span>
            <span className={llmStatus?.configured ? 'status-pill status-pill--approved' : 'status-pill status-pill--pending'}>
              {llmStatus?.configured ? t('LLM configured') : t('LLM setup required')}
            </span>
            <button type="button" className="btn btn-secondary" onClick={load} disabled={loading}>{t('Refresh')}</button>
          </div>
        </div>
      </div>

      {loading ? <LoadingState label={t('Loading Mentor Admin data...')} /> : null}
      <ErrorBanner message={error} />

      <div className="mentor-admin-layout">
        <aside className="panel workspace-panel mentor-admin-sidebar" aria-label={t('Mentor Admin sections')}>
          {sections.map((item) => (
            <button
              key={item.id}
              type="button"
              className={`mentor-admin-nav-item ${activeSection === item.id ? 'active' : ''}`}
              onClick={() => setActiveSection(item.id)}
            >
              <strong>{t(item.label)}</strong>
              <span>{t(item.summary)}</span>
            </button>
          ))}
        </aside>

        <div className="stack mentor-admin-content">
          {activeSection === 'dashboard' ? (
            <div className="admin-dashboard-grid">
              {dashboardMetrics.map(([label, value]) => (
                <article key={label} className="admin-metric-card">
                  <span className="helper">{t(label)}</span>
                  <strong>{t(value)}</strong>
                </article>
              ))}
            </div>
          ) : null}

          {activeSection === 'ai-mentors' ? (
            <PlaceholderPanel
              title={t('AI Mentors')}
              description={t('Create configurable AI mentor profiles from starter templates, activate or archive them, and review assignment and usage signals.')}
            >
              <div className="button-row">
                <button type="button" className="btn btn-primary" onClick={() => openNewAiMentorForm()}>{t('New AI mentor')}</button>
              </div>
              {aiMentors.length ? (
                <div className="admin-table-wrap">
                  <table className="admin-table">
                    <thead>
                      <tr>
                        {['AI mentor', 'Skill track', 'Status', 'Assigned learners', 'Usage', 'Actions'].map((column) => <th key={column}>{t(column)}</th>)}
                      </tr>
                    </thead>
                    <tbody>
                      {aiMentors.map((mentor) => (
                        <tr key={mentor.id}>
                          <td>
                            <strong>{mentor.name}</strong>
                            <p className="helper">{mentor.description || mentor.slug}</p>
                          </td>
                          <td>{mentor.skillName || mentor.skillTrack}</td>
                          <td><span className={`status-pill status-pill--${mentor.status === 'active' ? 'approved' : mentor.status === 'archived' ? 'rejected' : 'draft'}`}>{t(mentor.status)}</span></td>
                          <td>{formatNumber(mentor.activeAssignmentCount || 0)}</td>
                          <td>{formatNumber(mentor.totalTokens || 0)} {t('tokens')}</td>
                          <td>
                            <EntityActionMenu label={t('Actions')}>
                              <button type="button" onClick={() => setSelectedAiMentor(mentor)}>{t('View details')}</button>
                              <button type="button" onClick={() => openEditAiMentorForm(mentor)} disabled={busyId === mentor.id}>{t('Edit')}</button>
                              <button type="button" onClick={() => updateAiMentorStatus(mentor, 'active')} disabled={busyId === mentor.id || mentor.status === 'active'}>{t('Activate')}</button>
                              <button type="button" onClick={() => updateAiMentorStatus(mentor, 'archived')} disabled={busyId === mentor.id || mentor.status === 'archived'}>{t('Archive')}</button>
                            </EntityActionMenu>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <EmptyState title={t('No AI mentors yet')} description={t('Create a draft AI mentor from one of the starter templates.')} />
              )}
              <div className="admin-card-grid">
                {activeTemplates.map((template) => (
                  <article key={template.id} className="mentor-admin-track-card stack">
                    <div className="admin-panel-toolbar">
                      <h4 style={{ margin: 0 }}>{t(template.name)}</h4>
                      <span className="status-pill status-pill--draft">{t('Template')}</span>
                    </div>
                    <p className="helper">{template.description}</p>
                    <div className="button-row">
                      {(template.metadata?.focus || []).map((item: string) => <span key={item} className="status-pill status-pill--muted">{t(item)}</span>)}
                    </div>
                    <button type="button" className="btn btn-secondary" disabled={busyId === template.id} onClick={() => createFromTemplate(template)}>
                      {t('Create draft')}
                    </button>
                    <button type="button" className="btn btn-ghost" disabled={busyId === template.id} onClick={() => openNewAiMentorForm(template)}>
                      {t('Configure first')}
                    </button>
                  </article>
                ))}
              </div>
            </PlaceholderPanel>
          ) : null}

          {activeSection === 'human-mentors' ? (
            <PlaceholderPanel title={t('Human Mentors')} description={t('Approved human mentor claims and teaching readiness are listed here without turning human mentors into AI mentor records.')}>
              {pendingHumanMentors.length ? (
                <section className="stack" style={{ marginBottom: '1.25rem' }}>
                  <div className="admin-panel-toolbar">
                    <div>
                      <h4 style={{ margin: 0 }}>{t('Pending approvals')}</h4>
                      <p className="text-muted">{t('Human mentor claims waiting for review stay visible to Mentor Admin users.')}</p>
                    </div>
                    <span className="status-pill status-pill--pending">{formatNumber(pendingHumanMentors.length)}</span>
                  </div>
                  <div className="admin-table-wrap">
                    <table className="admin-table">
                      <thead><tr>{['Mentor', 'Skill', 'Status', 'Evidence', 'Submitted', 'Actions'].map((column) => <th key={column}>{t(column)}</th>)}</tr></thead>
                      <tbody>
                        {pendingHumanMentors.map((mentor) => (
                          <tr key={mentor.id}>
                            <td>{firstValue(mentor, ['workerName', 'mentorName', 'fullName', 'workerProfileId'], t('Human mentor'))}</td>
                            <td>{firstValue(mentor, ['skillName', 'canonicalSkillName', 'skillId'], t('Skill not set'))}</td>
                            <td><span className="status-pill status-pill--pending">{t(firstValue(mentor, ['status'], 'pending'))}</span></td>
                            <td>{firstValue(mentor, ['evidenceSummary', 'notes', 'claimNotes'], t('No evidence summary'))}</td>
                            <td>{firstValue(mentor, ['createdAt', 'updatedAt']) ? formatDate(firstValue(mentor, ['createdAt', 'updatedAt'])) : t('Not dated')}</td>
                            <td>
                              <EntityActionMenu label={t('Actions')}>
                                <button type="button" disabled={busyId === mentor.id} onClick={() => decideHumanMentorClaim(mentor, 'approve')}>{t('Approve')}</button>
                                <button type="button" disabled={busyId === mentor.id} onClick={() => decideHumanMentorClaim(mentor, 'reject')}>{t('Reject')}</button>
                              </EntityActionMenu>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </section>
              ) : null}
              {humanMentors.length ? (
                <div className="admin-table-wrap">
                  <table className="admin-table">
                    <thead><tr>{['Mentor', 'Skill', 'Status', 'Evidence', 'Updated', 'Actions'].map((column) => <th key={column}>{t(column)}</th>)}</tr></thead>
                    <tbody>
                      {humanMentors.map((mentor) => (
                        <tr key={mentor.id}>
                          <td>{firstValue(mentor, ['workerName', 'mentorName', 'fullName', 'workerProfileId'], t('Human mentor'))}</td>
                          <td>{firstValue(mentor, ['skillName', 'canonicalSkillName', 'skillId'], t('Skill not set'))}</td>
                          <td><span className="status-pill">{t(firstValue(mentor, ['status'], 'approved'))}</span></td>
                          <td>{firstValue(mentor, ['evidenceSummary', 'notes', 'claimNotes'], t('No evidence summary'))}</td>
                          <td>{firstValue(mentor, ['updatedAt', 'createdAt']) ? formatDate(firstValue(mentor, ['updatedAt', 'createdAt'])) : t('Not dated')}</td>
                          <td>
                            <EntityActionMenu label={t('Actions')}>
                              <button type="button" disabled={busyId === mentor.id} onClick={() => viewCoachImpact(mentor)}>{t('View coach impact')}</button>
                              <button type="button" disabled={busyId === mentor.id} onClick={() => openCoachingSessionForm(mentor)}>{t('Record coaching session')}</button>
                              <button type="button" disabled={busyId === mentor.id} onClick={() => decideHumanMentorClaim(mentor, 'suspend')}>{t('Suspend')}</button>
                            </EntityActionMenu>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : <EmptyState title={t('No approved human mentors yet')} description={t('Approved human mentor skill claims appear here.')} />}
            </PlaceholderPanel>
          ) : null}

          {activeSection === 'learners' ? (
            <PlaceholderPanel title={t('Apprentices / Learners')} description={t('Learners remain tied to existing identity, worker profile, and learning goal records while being managed from this separate product surface.')}>
              {learners.length ? (
                <div className="admin-table-wrap">
                  <table className="admin-table">
                    <thead><tr>{['Learner', 'Goal', 'Skill', 'Status', 'Target', 'Actions'].map((column) => <th key={column}>{t(column)}</th>)}</tr></thead>
                    <tbody>
                      {learners.map((learner) => (
                        <tr key={learner.id}>
                          <td>{firstValue(learner, ['workerName', 'learnerName', 'fullName', 'workerProfileId'], t('Learner'))}</td>
                          <td>{firstValue(learner, ['goalTitle', 'title', 'goal', 'description'], t('Learning goal'))}</td>
                          <td>{firstValue(learner, ['skillName', 'canonicalSkillName', 'skillId'], t('Skill not set'))}</td>
                          <td><span className="status-pill">{t(firstValue(learner, ['status'], 'active'))}</span></td>
                          <td>{firstValue(learner, ['targetDate', 'targetCompletionAt']) ? formatDate(firstValue(learner, ['targetDate', 'targetCompletionAt'])) : t('No target')}</td>
                          <td>
                            <EntityActionMenu label={t('Actions')}>
                              <button type="button" onClick={() => viewLearnerProgression(learner)}>{t('View progress')}</button>
                              <button type="button" onClick={() => generateLearnerRecommendations(learner)}>{t('Generate recommendations')}</button>
                            </EntityActionMenu>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : <EmptyState title={t('No learner goals yet')} description={t('Learner goals from the mentorship module appear here for Mentor Admin assignment.')} />}
            </PlaceholderPanel>
          ) : null}

          {activeSection === 'skills' ? (
            <PlaceholderPanel title={t('Skills')} description={t('Create and govern mentorship skill tracks used by AI mentors, human mentor claims, learning goals, and assignments.')}>
              <div className="button-row">
                <button type="button" className="btn btn-primary" onClick={() => { setFormError(''); setSkillForm(defaultSkillForm); setSkillFormOpen(true) }}>{t('New skill')}</button>
              </div>
              <section className="stack" style={{ marginTop: '1rem' }}>
                <div className="admin-panel-toolbar">
                  <div>
                    <h4 style={{ margin: 0 }}>{t('Starter AI mentor skill tracks')}</h4>
                    <p className="text-muted">{t('Seeded templates define the active AI mentor starter tracks and can be connected to tenant skills as the catalog grows.')}</p>
                  </div>
                  <span className="status-pill">{formatNumber(activeTemplates.length)}</span>
                </div>
                <div className="admin-card-grid">
                  {activeTemplates.map((template) => (
                    <article key={template.id} className="mentor-admin-track-card stack">
                      <div className="admin-panel-toolbar">
                        <h4 style={{ margin: 0 }}>{t(template.name)}</h4>
                        <span className="status-pill status-pill--draft">{template.skillTrack}</span>
                      </div>
                      <p className="helper">{template.description}</p>
                      <div className="button-row">
                        {(template.metadata?.focus || []).map((item: string) => <span key={item} className="status-pill status-pill--muted">{t(item)}</span>)}
                      </div>
                    </article>
                  ))}
                </div>
              </section>
              {skills.length ? (
                <div className="admin-table-wrap">
                  <table className="admin-table">
                    <thead><tr>{['Skill', 'Category', 'Description', 'Status', 'Actions'].map((column) => <th key={column}>{t(column)}</th>)}</tr></thead>
                    <tbody>
                      {skills.map((skill) => (
                        <tr key={skill.id}>
                          <td><strong>{skill.name}</strong></td>
                          <td>{skill.category || t('Uncategorized')}</td>
                          <td>{skill.description || t('No description')}</td>
                          <td><span className="status-pill">{t(skill.status || 'active')}</span></td>
                          <td>
                            <EntityActionMenu label={t('Actions')}>
                              <button type="button" disabled={busyId === skill.id || skill.status === 'active'} onClick={() => updateSkillStatus(skill, 'active')}>{t('Activate')}</button>
                              <button type="button" disabled={busyId === skill.id || skill.status === 'inactive'} onClick={() => updateSkillStatus(skill, 'inactive')}>{t('Mark inactive')}</button>
                              <button type="button" disabled={busyId === skill.id || skill.status === 'archived'} onClick={() => updateSkillStatus(skill, 'archived')}>{t('Archive')}</button>
                            </EntityActionMenu>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : <EmptyState title={t('No mentorship skills yet')} description={t('Create a skill to anchor learner goals, mentor rates, and AI mentor tracks.')} />}
            </PlaceholderPanel>
          ) : null}

          {activeSection === 'content-library' ? (
            <ContentLibraryPanel />
          ) : null}

          {activeSection === 'learning-paths' ? (
            <PlaceholderPanel
              title={t('Learning Paths')}
              description={t('Generate and persist AI-assisted upskilling roadmaps that can be tied to learners, assignments, and AI mentors.')}
            >
              <div className="button-row">
                <button type="button" className="btn btn-primary" onClick={openLearningPathForm}>{t('Generate learning path')}</button>
                <span className={llmStatus?.configured ? 'status-pill status-pill--approved' : 'status-pill status-pill--pending'}>
                  {llmStatus?.configured ? t('LLM configured') : t('LLM setup required')}
                </span>
              </div>
              <div className="mentor-admin-track-grid">
                <article className="mentor-admin-track-card stack">
                  <div>
                    <h4 style={{ margin: '0 0 .25rem' }}>{t('Lesson generator')}</h4>
                    <p className="text-muted">{t('Create a short lesson, examples, and a practice task for a selected module.')}</p>
                  </div>
                  <button type="button" className="btn btn-secondary" onClick={() => openAiActionForm('lesson')}>{t('Generate lesson')}</button>
                </article>
                <article className="mentor-admin-track-card stack">
                  <div>
                    <h4 style={{ margin: '0 0 .25rem' }}>{t('Quiz checkpoint')}</h4>
                    <p className="text-muted">{t('Create assessment questions, an answer key, and scoring guidance without certifying the learner.')}</p>
                  </div>
                  <button type="button" className="btn btn-secondary" onClick={() => openAiActionForm('quiz')}>{t('Generate quiz')}</button>
                </article>
                <article className="mentor-admin-track-card stack">
                  <div>
                    <h4 style={{ margin: '0 0 .25rem' }}>{t('Roleplay practice')}</h4>
                    <p className="text-muted">{t('Run interview, sales, or supervisor practice with feedback and a next prompt.')}</p>
                  </div>
                  <button type="button" className="btn btn-secondary" onClick={() => openAiActionForm('roleplay')}>{t('Start roleplay')}</button>
                </article>
                <article className="mentor-admin-track-card stack">
                  <div>
                    <h4 style={{ margin: '0 0 .25rem' }}>{t('Progress summary')}</h4>
                    <p className="text-muted">{t('Summarize recent learner activity into strengths, gaps, and next recommendations.')}</p>
                  </div>
                  <button type="button" className="btn btn-secondary" onClick={() => openAiActionForm('progress_summary')}>{t('Generate summary')}</button>
                </article>
                <article className="mentor-admin-track-card stack">
                  <div>
                    <h4 style={{ margin: '0 0 .25rem' }}>{t('French reading comprehension')}</h4>
                    <p className="text-muted">{t('Generate a Secondary 2 French passage, vocabulary, questions, answer key, and feedback rubric.')}</p>
                  </div>
                  <button type="button" className="btn btn-secondary" onClick={() => openAiActionForm('french_reading')}>{t('Run French reading')}</button>
                </article>
                <article className="mentor-admin-track-card stack">
                  <div>
                    <h4 style={{ margin: '0 0 .25rem' }}>{t('French writing correction')}</h4>
                    <p className="text-muted">{t('Correct a student paragraph with kind explanations, rewritten examples, and next practice.')}</p>
                  </div>
                  <button type="button" className="btn btn-secondary" onClick={() => openAiActionForm('french_writing')}>{t('Correct writing')}</button>
                </article>
                <article className="mentor-admin-track-card stack">
                  <div>
                    <h4 style={{ margin: '0 0 .25rem' }}>{t('French dictée')}</h4>
                    <p className="text-muted">{t('Create dictée text, vocabulary focus, rubric, and common mistakes to watch for.')}</p>
                  </div>
                  <button type="button" className="btn btn-secondary" onClick={() => openAiActionForm('french_dictee')}>{t('Generate dictée')}</button>
                </article>
                <article className="mentor-admin-track-card stack">
                  <div>
                    <h4 style={{ margin: '0 0 .25rem' }}>{t('Parent-friendly French summary')}</h4>
                    <p className="text-muted">{t('Summarize French strengths, gaps, recurring mistakes, and a practice plan for parent or guardian visibility.')}</p>
                  </div>
                  <button type="button" className="btn btn-secondary" onClick={() => openAiActionForm('french_progress')}>{t('French progress summary')}</button>
                </article>
              </div>
              {learningPaths.length ? (
                <div className="admin-table-wrap">
                  <table className="admin-table">
                    <thead><tr>{['Learner', 'Skill track', 'Goal', 'Timeline', 'Status', 'Updated', 'Actions'].map((column) => <th key={column}>{t(column)}</th>)}</tr></thead>
                    <tbody>
                      {learningPaths.map((path) => (
                        <tr key={path.id}>
                          <td>{path.learnerName || path.learnerProfileId || path.learnerUserId || t('Unassigned')}</td>
                          <td>{path.skillName || path.skillTrack}</td>
                          <td>{path.learnerGoal || t('No learner goal')}</td>
                          <td>{path.timeline || t('No timeline')}</td>
                          <td><span className="status-pill">{t(path.status)}</span></td>
                          <td>{path.updatedAt ? formatDate(path.updatedAt) : t('Not dated')}</td>
                          <td>
                            <EntityActionMenu label={t('Actions')}>
                              {(['draft', 'active', 'completed', 'archived'] as MentorLearningPath['status'][]).map((status) => (
                                <button key={status} type="button" disabled={busyId === path.id || path.status === status} onClick={() => updateLearningPathStatus(path, status)}>{t(status)}</button>
                              ))}
                            </EntityActionMenu>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : <EmptyState title={t('No learning paths yet')} description={t('Generate a roadmap from an AI mentor track, then save it as a tenant-scoped learning path.')} />}
            </PlaceholderPanel>
          ) : null}

          {activeSection === 'llm-settings' ? (
            <PlaceholderPanel
              title={t('LLM Settings')}
              description={llmStatus?.configured
                ? t('LLM provider is configured on the server. Secrets remain server-only and are not exposed here.')
                : t('No LLM provider is configured. AI mentor actions return a setup-required response until server env vars are set.')}
            >
              <div className="admin-dashboard-grid">
                <article className="admin-metric-card"><span className="helper">LLM_PROVIDER</span><strong>{llmStatus?.provider || t('Not configured')}</strong></article>
                <article className="admin-metric-card"><span className="helper">LLM_MODEL</span><strong>{llmStatus?.model || t('Not configured')}</strong></article>
                <article className="admin-metric-card"><span className="helper">LLM_API_KEY</span><strong>{llmStatus?.apiKeyConfigured ? t('Configured server-side') : t('Missing')}</strong></article>
                <article className="admin-metric-card"><span className="helper">LLM_BASE_URL</span><strong>{llmStatus?.baseUrlConfigured ? t('Configured server-side') : t('Default/provider')}</strong></article>
                <article className="admin-metric-card"><span className="helper">LLM_MAX_TOKENS</span><strong>{formatNumber(llmStatus?.maxTokens || 0)}</strong></article>
                <article className="admin-metric-card"><span className="helper">LLM_TEMPERATURE</span><strong>{String(llmStatus?.temperature ?? '-')}</strong></article>
              </div>
            </PlaceholderPanel>
          ) : null}

          {activeSection === 'assignments' ? (
            <PlaceholderPanel
              title={t('Assignments')}
              description={t('Assign learners to AI mentors or human mentors while keeping mentorship work separate from normal workforce admin operations.')}
            >
              <div className="button-row">
                <button type="button" className="btn btn-primary" onClick={() => { setFormError(''); setAssignmentForm(defaultAssignmentForm); setAssignmentFormOpen(true) }}>{t('New assignment')}</button>
              </div>
              {assignments.length ? (
                <div className="admin-table-wrap">
                  <table className="admin-table">
                    <thead><tr>{['Learner', 'Assignment', 'Skill', 'Learning goal', 'Status', 'Progress', 'Target', 'Last activity', 'Actions'].map((column) => <th key={column}>{t(column)}</th>)}</tr></thead>
                    <tbody>
                      {assignments.map((assignment) => (
                        <tr key={assignment.id}>
                          <td>{assignment.learnerName || assignment.learnerProfileId || assignment.learnerUserId || t('Learner')}</td>
                          <td>{assignment.assignmentType === 'ai_mentor' ? assignment.aiMentorName : assignment.humanMentorName}</td>
                          <td>{assignment.skillName || assignment.skillTrack || t('Not set')}</td>
                          <td>
                            {assignment.learningGoalId ? (
                              <div className="stack" style={{ gap: '.2rem' }}>
                                <span>{assignment.learningGoalSkillName || assignment.learningGoalId}</span>
                                {assignment.learningGoalStatus ? <span className="helper">{t(assignment.learningGoalStatus)}</span> : null}
                              </div>
                            ) : t('No linked goal')}
                          </td>
                          <td><span className="status-pill">{t(assignment.status)}</span></td>
                          <td>{assignment.progressSummary || t('No progress summary')}</td>
                          <td>{assignment.targetCompletionAt ? formatDate(assignment.targetCompletionAt) : t('No target')}</td>
                          <td>{assignment.lastInteractionAt ? formatDate(assignment.lastInteractionAt) : t('No activity yet')}</td>
                          <td>
                            <EntityActionMenu label={t('Actions')}>
                              {(['assigned', 'active', 'paused', 'completed', 'cancelled'] as MentorAssignment['status'][]).map((status) => (
                                <button key={status} type="button" disabled={busyId === assignment.id || assignment.status === status} onClick={() => updateAssignmentStatus(assignment, status)}>{t(status)}</button>
                              ))}
                            </EntityActionMenu>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : <EmptyState title={t('No mentor assignments yet')} description={t('Create an assignment to connect a learner to an AI mentor or human mentor.')} />}
            </PlaceholderPanel>
          ) : null}

          {activeSection === 'sessions' ? (
            <PlaceholderPanel title={t('Sessions')} description={t('Human mentorship session records stay connected here for scheduling and completion visibility.')}>
              <div className="button-row">
                <button type="button" className="btn btn-primary" onClick={() => openCoachingSessionForm()}>{t('Record coaching session')}</button>
              </div>
              {sessions.length ? (
                <div className="admin-table-wrap">
                  <table className="admin-table">
                    <thead><tr>{['Session', 'Mentor', 'Learner', 'Status', 'Starts'].map((column) => <th key={column}>{t(column)}</th>)}</tr></thead>
                    <tbody>
                      {sessions.map((session) => (
                        <tr key={session.id}>
                          <td>{firstValue(session, ['topic', 'title', 'id'], t('Session'))}</td>
                          <td>{firstValue(session, ['mentorName', 'mentorWorkerName', 'mentorWorkerProfileId'], t('Mentor'))}</td>
                          <td>{firstValue(session, ['learnerName', 'workerName', 'workerProfileId'], t('Learner'))}</td>
                          <td><span className="status-pill">{t(firstValue(session, ['status'], 'scheduled'))}</span></td>
                          <td>{firstValue(session, ['startsAt', 'scheduledStartAt', 'createdAt']) ? formatDate(firstValue(session, ['startsAt', 'scheduledStartAt', 'createdAt'])) : t('Not scheduled')}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : <EmptyState title={t('No sessions yet')} description={t('Human mentor sessions appear here when learners book or complete them.')} />}
            </PlaceholderPanel>
          ) : null}

          {activeSection === 'booking-requests' ? (
            <PlaceholderPanel title={t('Booking Requests')} description={t('Learner requests for human mentor time are listed separately from AI mentor assignment records.')}>
              {bookings.length ? (
                <div className="admin-table-wrap">
                  <table className="admin-table">
                    <thead><tr>{['Request', 'Learner', 'Mentor', 'Status', 'Preferred time'].map((column) => <th key={column}>{t(column)}</th>)}</tr></thead>
                    <tbody>
                      {bookings.map((booking) => (
                        <tr key={booking.id}>
                          <td>{firstValue(booking, ['skillName', 'topic', 'id'], t('Booking request'))}</td>
                          <td>{firstValue(booking, ['workerName', 'learnerName', 'workerProfileId'], t('Learner'))}</td>
                          <td>{firstValue(booking, ['mentorName', 'mentorWorkerProfileId'], t('Any mentor'))}</td>
                          <td><span className="status-pill">{t(firstValue(booking, ['status'], 'pending'))}</span></td>
                          <td>{firstValue(booking, ['preferredStart', 'preferredStartAt', 'createdAt']) ? formatDate(firstValue(booking, ['preferredStart', 'preferredStartAt', 'createdAt'])) : t('No preference')}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : <EmptyState title={t('No booking requests')} description={t('Learner requests for human mentor sessions appear here.')} />}
            </PlaceholderPanel>
          ) : null}

          {activeSection === 'reviews' ? (
            <PlaceholderPanel title={t('Reviews')} description={t('Mentor and learner reviews feed skill progression signals without granting certification automatically.')}>
              {reviews.length ? (
                <div className="admin-table-wrap">
                  <table className="admin-table">
                    <thead><tr>{['Reviewer', 'Reviewee', 'Type', 'Rating', 'Notes'].map((column) => <th key={column}>{t(column)}</th>)}</tr></thead>
                    <tbody>
                      {reviews.map((review) => (
                        <tr key={review.id}>
                          <td>{firstValue(review, ['reviewerName', 'reviewerWorkerProfileId'], t('Reviewer'))}</td>
                          <td>{firstValue(review, ['revieweeName', 'revieweeWorkerProfileId'], t('Reviewee'))}</td>
                          <td>{firstValue(review, ['reviewType'], t('Review'))}</td>
                          <td>{firstValue(review, ['rating'], t('Not rated'))}</td>
                          <td>{firstValue(review, ['notes'], t('No notes'))}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : <EmptyState title={t('No reviews yet')} description={t('Mentorship reviews appear here after sessions and matches produce feedback.')} />}
            </PlaceholderPanel>
          ) : null}

          {activeSection === 'rates-fees' ? (
            <PlaceholderPanel title={t('Rates & Fees')} description={t('Human mentor rates and product fee settings support the internal/off-platform mentor ledger.')}>
              {platformFeeSettings ? (
                <div className="button-row">
                  <span className="status-pill">{t('Platform fee settings loaded')}</span>
                  <span className="status-pill status-pill--muted">{firstValue(platformFeeSettings, ['feeMode', 'mode', 'status'], t('Fee settings'))}</span>
                </div>
              ) : null}
              {rates.length ? (
                <div className="admin-table-wrap">
                  <table className="admin-table">
                    <thead><tr>{['Mentor', 'Skill', 'Rate', 'Status'].map((column) => <th key={column}>{t(column)}</th>)}</tr></thead>
                    <tbody>
                      {rates.map((rate) => (
                        <tr key={rate.id}>
                          <td>{firstValue(rate, ['mentorName', 'workerName', 'mentorWorkerProfileId'], t('Mentor'))}</td>
                          <td>{firstValue(rate, ['skillName', 'skillId'], t('Skill'))}</td>
                          <td>{firstValue(rate, ['hourlyRate', 'rate', 'amount'], t('Not listed'))}</td>
                          <td><span className="status-pill">{t(firstValue(rate, ['status', 'isActive'], 'active'))}</span></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : <EmptyState title={t('No mentor rates yet')} description={t('Approved human mentor rates appear here.')} />}
            </PlaceholderPanel>
          ) : null}

          {activeSection === 'ledger' ? (
            <PlaceholderPanel title={t('Payments / Ledger')} description={t('Internal payment records are visible here for off-platform settlement tracking.')}>
              {payments.length ? (
                <div className="admin-table-wrap">
                  <table className="admin-table">
                    <thead><tr>{['Payment', 'Mentor', 'Learner', 'Amount', 'Status'].map((column) => <th key={column}>{t(column)}</th>)}</tr></thead>
                    <tbody>
                      {payments.map((payment) => (
                        <tr key={payment.id}>
                          <td>{firstValue(payment, ['id'], t('Payment'))}</td>
                          <td>{firstValue(payment, ['mentorName', 'mentorWorkerProfileId'], t('Mentor'))}</td>
                          <td>{firstValue(payment, ['workerName', 'learnerName', 'workerProfileId'], t('Learner'))}</td>
                          <td>{firstValue(payment, ['amount', 'grossAmount', 'mentorPayout'], t('Not set'))}</td>
                          <td><span className="status-pill">{t(firstValue(payment, ['status'], 'pending'))}</span></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : <EmptyState title={t('No ledger records yet')} description={t('Payment and settlement records appear here when mentorship sessions are payable or paid.')} />}
            </PlaceholderPanel>
          ) : null}

          {activeSection === 'reports' ? (
            <PlaceholderPanel
              title={t('Reports')}
              description={t('AI usage reporting is tenant-scoped and grouped by mentor, learner, skill track, tokens, and estimated cost.')}
            >
              <div className="admin-filter-bar">
                <label>{t('AI mentor')}
                  <select className="input" value={usageFilters.aiMentorId || ''} onChange={(event) => setUsageFilters({ ...usageFilters, aiMentorId: event.target.value })}>
                    <option value="">{t('All AI mentors')}</option>
                    {aiMentors.map((mentor) => <option key={mentor.id} value={mentor.id}>{mentor.name}</option>)}
                  </select>
                </label>
                {learnerProfileOptions.length ? (
                  <label>{t('Learner')}
                    <select className="input" value={usageFilters.learnerProfileId || ''} onChange={(event) => setUsageFilters({ ...usageFilters, learnerProfileId: event.target.value, learnerUserId: '' })}>
                      <option value="">{t('All learners')}</option>
                      {learnerProfileOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                    </select>
                  </label>
                ) : learnerOptions.length ? (
                  <label>{t('Learner user account')}
                    <select className="input" value={usageFilters.learnerUserId || ''} onChange={(event) => setUsageFilters({ ...usageFilters, learnerUserId: event.target.value, learnerProfileId: '' })}>
                      <option value="">{t('All learners')}</option>
                      {learnerOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                    </select>
                  </label>
                ) : null}
                <label>{t('Skill track')}
                  <select className="input" value={usageFilters.skillTrack || ''} onChange={(event) => setUsageFilters({ ...usageFilters, skillTrack: event.target.value })}>
                    <option value="">{t('All skill tracks')}</option>
                    {skillTrackOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                  </select>
                </label>
                <label>{t('From')}<input className="input" type="date" value={usageFilters.dateFrom || ''} onChange={(event) => setUsageFilters({ ...usageFilters, dateFrom: event.target.value })} /></label>
                <label>{t('To')}<input className="input" type="date" value={usageFilters.dateTo || ''} onChange={(event) => setUsageFilters({ ...usageFilters, dateTo: event.target.value })} /></label>
                <div className="button-row">
                  <button type="button" className="btn btn-secondary" onClick={() => loadUsage(usageFilters)} disabled={busyId === 'ai-usage-report'}>{t('Apply')}</button>
                  <button
                    type="button"
                    className="btn btn-ghost"
                    onClick={() => {
                      setUsageFilters(defaultUsageFilters)
                      loadUsage(defaultUsageFilters)
                    }}
                    disabled={busyId === 'ai-usage-report'}
                  >
                    {t('Clear')}
                  </button>
                </div>
              </div>
              {usage.length ? (
                <div className="admin-table-wrap">
                  <table className="admin-table">
                    <thead><tr>{['Date', 'AI mentor', 'Learner', 'Skill track', 'Type', 'Tokens', 'Cost'].map((column) => <th key={column}>{t(column)}</th>)}</tr></thead>
                    <tbody>
                      {usage.map((row) => (
                        <tr key={row.id}>
                          <td>{formatDate(row.createdAt)}</td>
                          <td>{row.aiMentorName || t('Unassigned')}</td>
                          <td>{row.learnerName || t('Unassigned')}</td>
                          <td>{row.skillTrack || t('Not set')}</td>
                          <td>{t(row.interactionType)}</td>
                          <td>{formatNumber(row.totalTokens || 0)}</td>
                          <td>{formatCurrency(row.estimatedCost || 0, 'USD')}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : <EmptyState title={t('No AI usage yet')} description={t('Usage rows appear only after configured LLM calls complete or fail after provider contact.')} />}
            </PlaceholderPanel>
          ) : null}

          {showCurrentMentorshipData ? (
            <>
              <PlaceholderPanel
                title={t(section.label)}
                description={t(`${section.summary} Current human mentorship data is powered by the existing mentorship/apprenticeship APIs, while AI mentor profiles and assignments use the Mentor Admin API.`)}
              >
                <p className="helper">{t('Relevant current module area')}: {t(legacyTabLabel || '')}</p>
              </PlaceholderPanel>
              <MentorshipPanel />
            </>
          ) : null}
        </div>
      </div>

      <FormModal
        title={aiMentorForm.id ? t('Edit AI mentor') : t('New AI mentor')}
        description={t('Configure the tenant-owned AI mentor profile, persona, languages, and safety copy.')}
        isOpen={aiMentorFormOpen}
        onClose={() => setAiMentorFormOpen(false)}
        size="lg"
        footer={(
          <>
            <button type="button" className="btn btn-ghost" onClick={() => setAiMentorFormOpen(false)} disabled={Boolean(busyId)}>{t('Cancel')}</button>
            <button type="button" className="btn btn-primary" onClick={saveAiMentorForm} disabled={Boolean(busyId)}>{t('Save AI mentor')}</button>
          </>
        )}
      >
        <ErrorBanner message={formError} />
        <div className="admin-form-grid">
          <label>{t('Template')}
            <select className="input" value={aiMentorForm.templateId} onChange={(event) => applyTemplateToAiMentorForm(event.target.value)}>
              <option value="">{t('Custom AI mentor')}</option>
              {activeTemplates.map((template) => <option key={template.id} value={template.id}>{template.name}</option>)}
            </select>
          </label>
          <label>{t('Status')}
            <select className="input" value={aiMentorForm.status} onChange={(event) => setAiMentorForm({ ...aiMentorForm, status: event.target.value as AiMentor['status'] })}>
              {['draft', 'active', 'archived'].map((status) => <option key={status} value={status}>{t(status)}</option>)}
            </select>
          </label>
          <label>{t('Name')}<input className="input" value={aiMentorForm.name} onChange={(event) => setAiMentorForm({ ...aiMentorForm, name: event.target.value })} /></label>
          <label>{t('Slug')}<input className="input" value={aiMentorForm.slug} onChange={(event) => setAiMentorForm({ ...aiMentorForm, slug: event.target.value })} /></label>
          <label>{t('Skill track')}<input className="input" value={aiMentorForm.skillTrack} onChange={(event) => setAiMentorForm({ ...aiMentorForm, skillTrack: event.target.value })} /></label>
          <label>{t('Default language')}<input className="input" value={aiMentorForm.defaultLanguage} onChange={(event) => setAiMentorForm({ ...aiMentorForm, defaultLanguage: event.target.value })} /></label>
          <label className="admin-form-wide">{t('Description')}<textarea className="input" rows={2} value={aiMentorForm.description} onChange={(event) => setAiMentorForm({ ...aiMentorForm, description: event.target.value })} /></label>
          <label className="admin-form-wide">{t('Persona / system prompt')}<textarea className="input" rows={5} value={aiMentorForm.personaPrompt} onChange={(event) => setAiMentorForm({ ...aiMentorForm, personaPrompt: event.target.value })} /></label>
          <label>{t('Supported languages')}<input className="input" value={aiMentorForm.supportedLanguages} onChange={(event) => setAiMentorForm({ ...aiMentorForm, supportedLanguages: event.target.value })} /></label>
          <label>{t('Difficulty levels')}<input className="input" value={aiMentorForm.difficultyLevels} onChange={(event) => setAiMentorForm({ ...aiMentorForm, difficultyLevels: event.target.value })} /></label>
          <label className="admin-form-wide">{t('Safety disclaimer')}<textarea className="input" rows={3} value={aiMentorForm.safetyDisclaimer} onChange={(event) => setAiMentorForm({ ...aiMentorForm, safetyDisclaimer: event.target.value })} /></label>
        </div>
      </FormModal>

      <FormModal
        title={t('Record coaching session')}
        description={t('Record human or peer coaching time as learner activity, evidence, and coach impact.')}
        isOpen={coachingSessionFormOpen}
        onClose={() => setCoachingSessionFormOpen(false)}
        size="lg"
        footer={(
          <>
            <button type="button" className="btn btn-ghost" onClick={() => setCoachingSessionFormOpen(false)} disabled={Boolean(busyId)}>{t('Cancel')}</button>
            <button type="button" className="btn btn-primary" onClick={saveCoachingSessionForm} disabled={Boolean(busyId)}>{t('Record coaching session')}</button>
          </>
        )}
      >
        <ErrorBanner message={formError} />
        <div className="admin-form-grid">
          {humanMentorOptions.length ? (
            <label>{t('Coach')}
              <select className="input" value={coachingSessionForm.coachWorkerProfileId} onChange={(event) => setCoachingSessionForm({ ...coachingSessionForm, coachWorkerProfileId: event.target.value })}>
                <option value="">{t('Choose coach')}</option>
                {humanMentorOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
              </select>
            </label>
          ) : (
            <label>{t('Coach worker profile id')}<input className="input" value={coachingSessionForm.coachWorkerProfileId} onChange={(event) => setCoachingSessionForm({ ...coachingSessionForm, coachWorkerProfileId: event.target.value })} /></label>
          )}
          {learnerProfileOptions.length ? (
            <label>{t('Learner profile')}
              <select className="input" value={coachingSessionForm.learnerWorkerProfileId} onChange={(event) => setCoachingSessionForm({ ...coachingSessionForm, learnerWorkerProfileId: event.target.value })}>
                <option value="">{t('Choose learner')}</option>
                {learnerProfileOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
              </select>
            </label>
          ) : (
            <label>{t('Learner worker profile id')}<input className="input" value={coachingSessionForm.learnerWorkerProfileId} onChange={(event) => setCoachingSessionForm({ ...coachingSessionForm, learnerWorkerProfileId: event.target.value })} /></label>
          )}
          <label>{t('Skill track')}<input className="input" value={coachingSessionForm.skillTrack} onChange={(event) => setCoachingSessionForm({ ...coachingSessionForm, skillTrack: event.target.value })} /></label>
          <label>{t('Duration minutes')}<input className="input" value={coachingSessionForm.durationMinutes} onChange={(event) => setCoachingSessionForm({ ...coachingSessionForm, durationMinutes: event.target.value })} /></label>
          <label>{t('Started at')}<input className="input" type="datetime-local" value={coachingSessionForm.startedAt} onChange={(event) => setCoachingSessionForm({ ...coachingSessionForm, startedAt: event.target.value })} /></label>
          <label>{t('Ended at')}<input className="input" type="datetime-local" value={coachingSessionForm.endedAt} onChange={(event) => setCoachingSessionForm({ ...coachingSessionForm, endedAt: event.target.value })} /></label>
          <label>{t('Learner score before')}<input className="input" value={coachingSessionForm.learnerScoreBefore} onChange={(event) => setCoachingSessionForm({ ...coachingSessionForm, learnerScoreBefore: event.target.value })} /></label>
          <label>{t('Learner score after')}<input className="input" value={coachingSessionForm.learnerScoreAfter} onChange={(event) => setCoachingSessionForm({ ...coachingSessionForm, learnerScoreAfter: event.target.value })} /></label>
          <label className="admin-form-wide">{t('AI-generated session summary')}<textarea className="input" rows={3} value={coachingSessionForm.aiSummary} onChange={(event) => setCoachingSessionForm({ ...coachingSessionForm, aiSummary: event.target.value })} /></label>
          <label className="admin-form-wide">{t('Notes')}<textarea className="input" rows={3} value={coachingSessionForm.notes} onChange={(event) => setCoachingSessionForm({ ...coachingSessionForm, notes: event.target.value })} /></label>
        </div>
      </FormModal>

      <DetailDrawer
        title={selectedAiMentor?.name || t('AI mentor details')}
        subtitle={selectedAiMentor ? `${selectedAiMentor.skillName || selectedAiMentor.skillTrack} - ${t(selectedAiMentor.status)}` : undefined}
        isOpen={Boolean(selectedAiMentor)}
        onClose={() => setSelectedAiMentor(null)}
        actions={selectedAiMentor ? (
          <>
            <button type="button" className="btn btn-secondary" onClick={() => openEditAiMentorForm(selectedAiMentor)}>{t('Edit')}</button>
            <button type="button" className="btn btn-ghost" onClick={() => updateAiMentorStatus(selectedAiMentor, 'active')} disabled={selectedAiMentor.status === 'active' || busyId === selectedAiMentor.id}>{t('Activate')}</button>
            <button type="button" className="btn btn-ghost" onClick={() => updateAiMentorStatus(selectedAiMentor, 'archived')} disabled={selectedAiMentor.status === 'archived' || busyId === selectedAiMentor.id}>{t('Archive')}</button>
          </>
        ) : null}
      >
        {selectedAiMentor ? (
          <div className="stack">
            <div className="admin-detail-grid">
              <div><span className="helper">{t('Slug')}</span><strong>{selectedAiMentor.slug}</strong></div>
              <div><span className="helper">{t('Default language')}</span><strong>{selectedAiMentor.defaultLanguage || t('Not set')}</strong></div>
              <div><span className="helper">{t('Supported languages')}</span><strong>{(selectedAiMentor.supportedLanguages || []).join(', ') || t('Not set')}</strong></div>
              <div><span className="helper">{t('Difficulty levels')}</span><strong>{(selectedAiMentor.difficultyLevels || []).join(', ') || t('Not set')}</strong></div>
              <div><span className="helper">{t('Assigned learners')}</span><strong>{formatNumber(selectedAiMentor.activeAssignmentCount || selectedAiMentorAssignments.length)}</strong></div>
              <div><span className="helper">{t('Usage')}</span><strong>{formatNumber(selectedAiMentor.totalTokens || 0)} {t('tokens')}</strong></div>
              <div><span className="helper">{t('Estimated cost')}</span><strong>{formatCurrency(selectedAiMentor.estimatedCost || 0, 'USD')}</strong></div>
              <div><span className="helper">{t('Provider settings')}</span><strong>{Object.keys(selectedAiMentor.providerSettings || {}).length ? t('Configured') : t('Default/provider')}</strong></div>
            </div>
            <section className="stack">
              <h4>{t('Persona / system prompt')}</h4>
              <pre className="admin-code-block">{selectedAiMentor.personaPrompt || t('No persona prompt')}</pre>
            </section>
            <section className="stack">
              <h4>{t('Safety disclaimer')}</h4>
              <p className="text-muted">{selectedAiMentor.safetyDisclaimer || t('Educational guidance only. Human validation is required for regulated work.')}</p>
            </section>
            <section className="stack">
              <h4>{t('Assigned learners')}</h4>
              {selectedAiMentorAssignments.length ? (
                <div className="admin-table-wrap">
                  <table className="admin-table">
                    <thead><tr>{['Learner', 'Status', 'Skill track', 'Last activity'].map((column) => <th key={column}>{t(column)}</th>)}</tr></thead>
                    <tbody>
                      {selectedAiMentorAssignments.map((assignment) => (
                        <tr key={assignment.id}>
                          <td>{assignment.learnerName || assignment.learnerProfileId || assignment.learnerUserId || t('Learner')}</td>
                          <td><span className="status-pill">{t(assignment.status)}</span></td>
                          <td>{assignment.skillTrack || selectedAiMentor.skillTrack}</td>
                          <td>{assignment.lastInteractionAt ? formatDate(assignment.lastInteractionAt) : t('No activity yet')}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : <EmptyState title={t('No assigned learners yet')} description={t('AI mentor assignments will appear here after learners are connected.')} />}
            </section>
            <section className="stack">
              <h4>{t('Recent AI usage')}</h4>
              {selectedAiMentorUsage.length ? (
                <div className="admin-table-wrap">
                  <table className="admin-table">
                    <thead><tr>{['Date', 'Learner', 'Type', 'Tokens', 'Cost'].map((column) => <th key={column}>{t(column)}</th>)}</tr></thead>
                    <tbody>
                      {selectedAiMentorUsage.map((row) => (
                        <tr key={row.id}>
                          <td>{formatDate(row.createdAt)}</td>
                          <td>{row.learnerName || row.learnerProfileId || row.learnerUserId || t('Unassigned')}</td>
                          <td>{t(row.interactionType)}</td>
                          <td>{formatNumber(row.totalTokens || 0)}</td>
                          <td>{formatCurrency(row.estimatedCost || 0, 'USD')}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : <EmptyState title={t('No AI usage yet')} description={t('Usage rows appear here after this AI mentor runs LLM-backed actions.')} />}
            </section>
          </div>
        ) : null}
      </DetailDrawer>

      <DetailDrawer
        title={selectedLearnerProgression?.learner ? firstValue(selectedLearnerProgression.learner, ['learnerName', 'workerName', 'fullName', 'learnerProfileId'], t('Learner progress')) : t('Learner progress')}
        subtitle={t('Skill progression, recommendations, learning paths, activities, and coaching minutes')}
        isOpen={Boolean(selectedLearnerProgression)}
        onClose={() => setSelectedLearnerProgression(null)}
      >
        {selectedLearnerProgression ? (
          <div className="stack">
            <div className="admin-detail-grid">
              <div><span className="helper">{t('Coaching minutes received')}</span><strong>{formatNumber(Number(selectedLearnerProgression.coaching?.coachingMinutesReceived || 0))}</strong></div>
              <div><span className="helper">{t('Coaching minutes taught')}</span><strong>{formatNumber(Number(selectedLearnerProgression.coaching?.coachingMinutesTaught || 0))}</strong></div>
              <div><span className="helper">{t('Active learning paths')}</span><strong>{formatNumber(selectedLearnerProgression.learningPaths?.filter((path) => path.status === 'active').length || 0)}</strong></div>
              <div><span className="helper">{t('Current recommendations')}</span><strong>{formatNumber(selectedLearnerProgression.recommendations?.length || 0)}</strong></div>
            </div>
            <section className="stack">
              <h4>{t('Current mentorship setup')}</h4>
              <div className="admin-detail-grid">
                <div>
                  <span className="helper">{t('Active learning path')}</span>
                  <strong>{learnerCurrentPlan.activePath?.learnerGoal || learnerCurrentPlan.activePath?.skillTrack || t('Not assigned')}</strong>
                  {learnerCurrentPlan.activePath?.timeline ? <span className="helper">{learnerCurrentPlan.activePath.timeline}</span> : null}
                </div>
                <div>
                  <span className="helper">{t('Assigned AI mentor')}</span>
                  <strong>{learnerCurrentPlan.activeAiAssignment?.aiMentorName || t('Not assigned')}</strong>
                  {learnerCurrentPlan.activeAiAssignment?.skillTrack ? <span className="helper">{learnerCurrentPlan.activeAiAssignment.skillTrack}</span> : null}
                </div>
                <div>
                  <span className="helper">{t('Assigned human mentor')}</span>
                  <strong>{learnerCurrentPlan.activeHumanAssignment?.humanMentorName || t('Not assigned')}</strong>
                  {learnerCurrentPlan.activeHumanAssignment?.skillTrack ? <span className="helper">{learnerCurrentPlan.activeHumanAssignment.skillTrack}</span> : null}
                </div>
                <div>
                  <span className="helper">{t('Next recommended action')}</span>
                  <strong>{learnerCurrentPlan.topRecommendation?.suggestedNextAction || t('No recommendation yet')}</strong>
                  {learnerCurrentPlan.topRecommendation?.priority ? <span className="helper">{t(learnerCurrentPlan.topRecommendation.priority)}</span> : null}
                </div>
              </div>
            </section>
            {selectedLearnerProgression.progressSummary ? (
              <section className="card stack">
                <div className="button-row" style={{ justifyContent: 'space-between' }}>
                  <h4 style={{ margin: 0 }}>{t('Progress summary')}</h4>
                  {selectedLearnerProgression.progressSummary.generatedAt ? <span className="helper">{formatDate(selectedLearnerProgression.progressSummary.generatedAt)}</span> : null}
                </div>
                {selectedLearnerProgression.progressSummary.parentFriendlySummary ? <p className="text-muted">{selectedLearnerProgression.progressSummary.parentFriendlySummary}</p> : null}
                <div className="admin-detail-grid">
                  <div><span className="helper">{t('Current focus')}</span><strong>{selectedLearnerProgression.progressSummary.currentFocus || t('Not set')}</strong></div>
                  <div><span className="helper">{t('Activities')}</span><strong>{formatNumber(Number(selectedLearnerProgression.progressSummary.metrics?.activitiesCompleted || 0))}</strong></div>
                  <div><span className="helper">{t('Assessments')}</span><strong>{formatNumber(Number(selectedLearnerProgression.progressSummary.metrics?.assessmentsCompleted || 0))}</strong></div>
                  <div><span className="helper">{t('Practice minutes')}</span><strong>{formatNumber(Number(selectedLearnerProgression.progressSummary.metrics?.practiceMinutes || 0))}</strong></div>
                  <div><span className="helper">{t('XP earned')}</span><strong>{formatNumber(Number(selectedLearnerProgression.progressSummary.metrics?.xpEarned || 0))}</strong></div>
                  <div><span className="helper">{t('Current streak')}</span><strong>{formatNumber(Number(selectedLearnerProgression.progressSummary.metrics?.currentStreakDays || 0))} {t('days')}</strong></div>
                  <div><span className="helper">{t('Evidence')}</span><strong>{formatNumber(Number(selectedLearnerProgression.progressSummary.metrics?.evidenceRecords || 0))}</strong></div>
                  <div><span className="helper">{t('Coaching minutes')}</span><strong>{formatNumber(Number(selectedLearnerProgression.progressSummary.metrics?.coachingMinutesReceived || 0))}</strong></div>
                </div>
                {selectedLearnerProgression.progressSummary.recommendedPracticePlan?.length ? (
                  <div className="stack">
                    <strong>{t('Recommended practice plan')}</strong>
                    <ul className="text-muted" style={{ margin: 0, paddingLeft: '1.25rem' }}>
                      {selectedLearnerProgression.progressSummary.recommendedPracticePlan.map((item) => <li key={item}>{item}</li>)}
                    </ul>
                  </div>
                ) : null}
              </section>
            ) : null}
            <section className="stack">
              <h4>{t('Assigned mentors and learning paths')}</h4>
              {selectedLearnerProgression.assignments?.length ? (
                <div className="admin-table-wrap">
                  <table className="admin-table">
                    <thead><tr>{['Type', 'Mentor', 'Skill', 'Status', 'Progress', 'Target', 'Last activity'].map((column) => <th key={column}>{t(column)}</th>)}</tr></thead>
                    <tbody>
                      {selectedLearnerProgression.assignments.map((assignment) => (
                        <tr key={assignment.id}>
                          <td>{assignment.assignmentType === 'ai_mentor' ? t('AI mentor') : t('Human mentor')}</td>
                          <td>{assignment.aiMentorName || assignment.humanMentorName || t('Mentor not assigned')}</td>
                          <td>{assignment.skillTrack || t('Skill not set')}</td>
                          <td><span className="status-pill">{t(assignment.status || 'assigned')}</span></td>
                          <td>{assignment.progressSummary || t('No progress summary')}</td>
                          <td>{assignment.targetCompletionAt ? formatDate(assignment.targetCompletionAt) : t('No target')}</td>
                          <td>{assignment.lastInteractionAt ? formatDate(assignment.lastInteractionAt) : t('No activity yet')}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : <EmptyState title={t('No assigned mentors yet')} description={t('AI mentor and human coach assignments appear here when connected to this learner.')} />}
              {selectedLearnerProgression.learningPaths?.length ? (
                <div className="admin-table-wrap">
                  <table className="admin-table">
                    <thead><tr>{['Learning path', 'Goal', 'Timeline', 'Status', 'Updated'].map((column) => <th key={column}>{t(column)}</th>)}</tr></thead>
                    <tbody>
                      {selectedLearnerProgression.learningPaths.map((path) => {
                        const roadmapPreview = learningPathRoadmapPreview(path.roadmap)
                        return (
                          <tr key={path.id}>
                            <td>{path.skillTrack || t('Learning path')}</td>
                            <td>
                              <div className="stack" style={{ gap: '.35rem' }}>
                                <span>{path.learnerGoal || t('Goal not set')}</span>
                                {roadmapPreview ? (
                                  <div className="text-muted">
                                    {roadmapPreview.modules.length ? <div>{t('Modules')}: {roadmapPreview.modules.join(', ')}</div> : null}
                                    {roadmapPreview.milestones.length ? <div>{t('Milestones')}: {roadmapPreview.milestones.join(', ')}</div> : null}
                                    {roadmapPreview.practiceTasks.length ? <div>{t('Practice tasks')}: {roadmapPreview.practiceTasks.join(', ')}</div> : null}
                                  </div>
                                ) : null}
                              </div>
                            </td>
                            <td>{path.timeline || t('Timeline not set')}</td>
                            <td><span className="status-pill">{t(path.status || 'draft')}</span></td>
                            <td>{path.updatedAt ? formatDate(path.updatedAt) : t('Not updated')}</td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              ) : null}
            </section>
            <section className="stack">
              <div className="admin-panel-toolbar">
                <div>
                  <h4 style={{ margin: 0 }}>{t('Recommended next skills/actions')}</h4>
                  <p className="text-muted">{t('Filter by the evidence source behind each recommendation.')}</p>
                </div>
                <div className="button-row">
                  {([
                    ['all', 'All'],
                    ['ai', 'AI evidence'],
                    ['activity', 'Activity evidence'],
                    ['coaching', 'Coaching'],
                  ] as Array<[RecommendationEvidenceFilter, string]>).map(([filter, label]) => (
                    <button
                      key={filter}
                      type="button"
                      className={`btn ${recommendationEvidenceFilter === filter ? 'btn-primary' : 'btn-secondary'}`}
                      onClick={() => setRecommendationEvidenceFilter(filter)}
                    >
                      {t(label)}
                    </button>
                  ))}
                </div>
              </div>
              {selectedLearnerProgression.recommendations?.length ? (
                filteredLearnerRecommendations.length ? (
                  <div className="admin-table-wrap">
                    <table className="admin-table">
                      <thead><tr>{['Skill', 'Type', 'Priority', 'Reason', 'Next action', 'Effort', 'Source'].map((column) => <th key={column}>{t(column)}</th>)}</tr></thead>
                      <tbody>
                        {filteredLearnerRecommendations.map((recommendation) => {
                          const insights = recommendationAiInsights(recommendation)
                          return (
                            <tr key={recommendation.id}>
                              <td>{recommendation.skillName || recommendation.skillTrack || t('Skill')}</td>
                              <td>{t(recommendation.recommendationType)}</td>
                              <td>
                                <div className="stack" style={{ gap: '.25rem' }}>
                                  <span className="status-pill">{t(recommendation.priority)}</span>
                                  <span className="helper">{formatNumber(Math.round(Number(recommendation.confidence || 0) * 100))}% {t('confidence')}</span>
                                </div>
                              </td>
                              <td>
                                <div className="stack" style={{ gap: '.35rem' }}>
                                  <span>{recommendation.reason}</span>
                                  {insights ? (
                                    <div className="text-muted">
                                      <strong>{t('AI insight evidence')}</strong>
                                      {insights.weaknesses.length ? <div>{t('Weaknesses')}: {insights.weaknesses.join(', ')}</div> : null}
                                      {insights.mistakes.length ? <div>{t('Mistakes')}: {insights.mistakes.join(', ')}</div> : null}
                                      {insights.failedQuestions.length ? <div>{t('Missed questions')}: {insights.failedQuestions.join(', ')}</div> : null}
                                      {insights.practicePlan.length ? <div>{t('Practice plan')}: {insights.practicePlan.join(', ')}</div> : null}
                                    </div>
                                  ) : null}
                                </div>
                              </td>
                              <td>{recommendation.suggestedNextAction}</td>
                              <td>{recommendation.estimatedEffort || t('Not estimated')}</td>
                              <td>{t(recommendation.source || 'deterministic')}</td>
                            </tr>
                          )
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : <EmptyState title={t('No recommendations match this filter')} description={t('Choose another evidence source to inspect learner recommendations.')} />
              ) : <EmptyState title={t('No recommendations yet')} description={t('Generate recommendations to create deterministic next actions from progression, evidence, learning paths, and coaching data.')} />}
            </section>
            <section className="stack">
              <h4>{t('Skill levels')}</h4>
              {selectedLearnerProgression.progression?.length ? (
                <div className="admin-table-wrap">
                  <table className="admin-table">
                    <thead><tr>{['Skill', 'Level', 'Rank', 'Evidence score', 'Mentorship minutes', 'Next level'].map((column) => <th key={column}>{t(column)}</th>)}</tr></thead>
                    <tbody>
                      {selectedLearnerProgression.progression.map((skill) => (
                        <tr key={skill.skillId}>
                          <td>{skill.skillName}</td>
                          <td>{t(skill.currentLevel || 'apprentice')}</td>
                          <td>{formatNumber(Number(skill.currentLevelRank || 0))}</td>
                          <td>{formatNumber(Number(skill.evidenceScore || 0))}</td>
                          <td>{formatNumber(Number(skill.mentorshipMinutesCompleted || 0))}</td>
                          <td>
                            <div className="stack" style={{ gap: '.35rem' }}>
                              <span>{skill.nextLevel?.label || skill.nextLevel?.levelKey || t('Not set')}</span>
                              {skill.nextLevelRequirements?.length ? (
                                <div className="text-muted">
                                  <strong>{t('Requirements')}</strong>
                                  {skill.nextLevelRequirements.slice(0, 3).map((requirement: MentorAdminRecord, index: number) => (
                                    <div key={`${skill.skillId}-requirement-${index}`}>{firstValue(requirement, ['label', 'description', 'requirement', 'type'], t('Requirement'))}</div>
                                  ))}
                                </div>
                              ) : null}
                              {skill.recommendedActions?.length ? (
                                <div className="text-muted">
                                  <strong>{t('Suggested actions')}</strong>
                                  {skill.recommendedActions.slice(0, 3).map((action: MentorAdminRecord, index: number) => (
                                    <div key={`${skill.skillId}-action-${index}`}>{firstValue(action, ['label', 'description', 'action', 'type'], t('Action'))}</div>
                                  ))}
                                </div>
                              ) : null}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : <EmptyState title={t('No skill progression yet')} description={t('Skill progression appears when worker skill profiles or learning evidence exist.')} />}
            </section>
            <section className="stack">
              <h4>{t('Evidence summary')}</h4>
              <div className="admin-detail-grid">
                <div><span className="helper">{t('Evidence records')}</span><strong>{formatNumber(Number(selectedLearnerProgression.evidenceSummary?.total || 0))}</strong></div>
                <div><span className="helper">{t('Evidence weight')}</span><strong>{formatNumber(Number(selectedLearnerProgression.evidenceSummary?.totalWeight || 0))}</strong></div>
                <div><span className="helper">{t('Score delta')}</span><strong>{formatNumber(Number(selectedLearnerProgression.evidenceSummary?.totalScoreDelta || 0))}</strong></div>
                <div><span className="helper">{t('Verified records')}</span><strong>{formatNumber(Number(selectedLearnerProgression.evidenceSummary?.byStatus?.verified || 0))}</strong></div>
              </div>
              {selectedLearnerProgression.evidence?.length ? (
                <div className="admin-table-wrap">
                  <table className="admin-table">
                    <thead><tr>{['Evidence', 'Skill', 'Status', 'Weight', 'Score delta', 'Source', 'Recorded'].map((column) => <th key={column}>{t(column)}</th>)}</tr></thead>
                    <tbody>
                      {selectedLearnerProgression.evidence.map((item) => (
                        <tr key={item.id}>
                          <td>{t(item.evidenceType || 'evidence')}</td>
                          <td>{item.skillName || item.skillTrack || t('Skill')}</td>
                          <td><span className="status-pill">{t(item.evidenceStatus || 'recorded')}</span></td>
                          <td>{formatNumber(Number(item.evidenceWeight || 0))}</td>
                          <td>{formatNumber(Number(item.scoreDelta || 0))}</td>
                          <td>{t(item.source || 'platform')}</td>
                          <td>{item.occurredAt ? formatDate(item.occurredAt) : t('Not dated')}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : <EmptyState title={t('No evidence records yet')} description={t('Recorded activities, coaching sessions, mentor reviews, and verified samples appear here as canonical learner evidence.')} />}
            </section>
            <section className="stack">
              <div className="admin-panel-toolbar">
                <h4 style={{ margin: 0 }}>{t('Completed activities')}</h4>
                <button type="button" className="btn btn-secondary" onClick={() => { setFormError(''); setLearningActivityForm(defaultLearningActivityForm); setLearningActivityFormOpen(true) }}>{t('Record activity')}</button>
              </div>
              {selectedLearnerProgression.activities?.length ? (
                <div className="admin-table-wrap">
                  <table className="admin-table">
                    <thead><tr>{['Activity', 'Skill', 'Score', 'XP', 'Mastery', 'Duration', 'Completed'].map((column) => <th key={column}>{t(column)}</th>)}</tr></thead>
                    <tbody>
                      {selectedLearnerProgression.activities.map((activity) => (
                        <tr key={activity.id}>
                          <td>{t(activity.activityType || 'Activity')}</td>
                          <td>{activity.skillName || activity.skillTrack || t('Skill')}</td>
                          <td>{activity.score != null && activity.maxScore != null ? `${activity.score}/${activity.maxScore}` : t('Not scored')}</td>
                          <td>{formatNumber(Number(activity.xpEarned || 0))}</td>
                          <td>{activity.masteryPercent != null ? `${formatNumber(Number(activity.masteryPercent))}%` : t('Not set')}</td>
                          <td>{activity.durationMinutes != null ? `${formatNumber(Number(activity.durationMinutes))} ${t('minutes')}` : t('Not timed')}</td>
                          <td>{activity.completedAt ? formatDate(activity.completedAt) : t('Not dated')}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : <EmptyState title={t('No completed activities yet')} description={t('AI lessons, quizzes, writing submissions, dictées, coaching sessions, and parent/admin notes can be recorded as learning evidence.')} />}
            </section>
          </div>
        ) : null}
      </DetailDrawer>

      <FormModal
        title={t('Record learning activity')}
        description={t('Add learner evidence from AI practice, quizzes, writing, dictée, coaching, reviews, uploaded work, or parent/admin notes.')}
        isOpen={learningActivityFormOpen}
        onClose={() => setLearningActivityFormOpen(false)}
        footer={(
          <>
            <button type="button" className="btn btn-ghost" onClick={() => setLearningActivityFormOpen(false)} disabled={Boolean(busyId)}>{t('Cancel')}</button>
            <button type="button" className="btn btn-primary" onClick={saveLearningActivity} disabled={Boolean(busyId)}>{t('Record activity')}</button>
          </>
        )}
      >
        <ErrorBanner message={formError} />
        <div className="admin-form-grid">
          <label>{t('Activity type')}
            <select className="input" value={learningActivityForm.activityType} onChange={(event) => setLearningActivityForm({ ...learningActivityForm, activityType: event.target.value as MentorLearningActivity['activityType'] })}>
              {[
                'ai_lesson_completion',
                'ai_quiz_checkpoint',
                'writing_submission',
                'reading_comprehension_result',
                'dictee_result',
                'human_coaching_session',
                'mentor_review',
                'uploaded_work_sample',
                'admin_parent_note',
              ].map((activityType) => <option key={activityType} value={activityType}>{t(activityType)}</option>)}
            </select>
          </label>
          <label>{t('Skill track')}<input className="input" value={learningActivityForm.skillTrack} onChange={(event) => setLearningActivityForm({ ...learningActivityForm, skillTrack: event.target.value })} /></label>
          <label>{t('Skill id')}<input className="input" value={learningActivityForm.skillId} onChange={(event) => setLearningActivityForm({ ...learningActivityForm, skillId: event.target.value })} /></label>
          <label>{t('Assignment id')}<input className="input" value={learningActivityForm.assignmentId} onChange={(event) => setLearningActivityForm({ ...learningActivityForm, assignmentId: event.target.value })} /></label>
          <label className="admin-form-wide">{t('Title')}<input className="input" value={learningActivityForm.title} onChange={(event) => setLearningActivityForm({ ...learningActivityForm, title: event.target.value })} /></label>
          <label>{t('Score')}<input className="input" value={learningActivityForm.score} onChange={(event) => setLearningActivityForm({ ...learningActivityForm, score: event.target.value })} /></label>
          <label>{t('Max score')}<input className="input" value={learningActivityForm.maxScore} onChange={(event) => setLearningActivityForm({ ...learningActivityForm, maxScore: event.target.value })} /></label>
          <label>{t('XP earned')}<input className="input" value={learningActivityForm.xpEarned} onChange={(event) => setLearningActivityForm({ ...learningActivityForm, xpEarned: event.target.value })} /></label>
          <label>{t('Mastery percent')}<input className="input" value={learningActivityForm.masteryPercent} onChange={(event) => setLearningActivityForm({ ...learningActivityForm, masteryPercent: event.target.value })} /></label>
          <label>{t('Duration minutes')}<input className="input" value={learningActivityForm.durationMinutes} onChange={(event) => setLearningActivityForm({ ...learningActivityForm, durationMinutes: event.target.value })} /></label>
        </div>
      </FormModal>

      <DetailDrawer
        title={selectedCoachImpact?.coach ? firstValue(selectedCoachImpact.coach, ['coachName', 'workerName', 'fullName', 'coachProfileId'], t('Coach impact')) : t('Coach impact')}
        subtitle={t('Human/peer coaching time and learner improvement, separate from workforce reliability')}
        isOpen={Boolean(selectedCoachImpact)}
        onClose={() => setSelectedCoachImpact(null)}
      >
        {selectedCoachImpact ? (
          <div className="stack">
            <div className="admin-detail-grid">
              <div><span className="helper">{t('Learners coached')}</span><strong>{formatNumber(Number(selectedCoachImpact.summary.learnersCoached || 0))}</strong></div>
              <div><span className="helper">{t('Coaching minutes')}</span><strong>{formatNumber(Number(selectedCoachImpact.summary.coachingMinutes || 0))}</strong></div>
              <div><span className="helper">{t('Completion rate')}</span><strong>{Number(selectedCoachImpact.summary.completionRate || 0).toFixed(1)}%</strong></div>
              <div><span className="helper">{t('Repeat learner rate')}</span><strong>{Number(selectedCoachImpact.summary.repeatLearnerRate || 0).toFixed(1)}%</strong></div>
              <div><span className="helper">{t('Average learner improvement')}</span><strong>{Number(selectedCoachImpact.summary.averageLearnerImprovement || 0).toFixed(1)}</strong></div>
              <div><span className="helper">{t('Mentorship review rating')}</span><strong>{Number(selectedCoachImpact.summary.averageMentorRating || 0).toFixed(1)}</strong></div>
            </div>
            {selectedCoachImpact.skillsCoached?.length ? (
              <div className="admin-table-wrap">
                <table className="admin-table">
                  <thead><tr>{['Skill', 'Sessions', 'Minutes'].map((column) => <th key={column}>{t(column)}</th>)}</tr></thead>
                  <tbody>
                    {selectedCoachImpact.skillsCoached.map((skill) => (
                      <tr key={skill.skillName}>
                        <td>{skill.skillName}</td>
                        <td>{formatNumber(Number(skill.sessions || 0))}</td>
                        <td>{formatNumber(Number(skill.minutes || 0))}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : <EmptyState title={t('No coaching sessions yet')} description={t('Record human or peer coaching sessions to measure coaching time and impact.')} />}
            {selectedCoachImpact.recentSessions?.length ? (
              <section className="stack">
                <h4>{t('Recent coaching sessions')}</h4>
                <div className="admin-table-wrap">
                  <table className="admin-table">
                    <thead><tr>{['Learner', 'Skill', 'Duration', 'Scores', 'AI summary', 'Recorded'].map((column) => <th key={column}>{t(column)}</th>)}</tr></thead>
                    <tbody>
                      {selectedCoachImpact.recentSessions.map((session) => (
                        <tr key={session.id}>
                          <td>{session.learnerName || t('Learner')}</td>
                          <td>{session.skillName || session.skillTrack || t('Skill not set')}</td>
                          <td>{session.durationMinutes != null ? `${formatNumber(Number(session.durationMinutes))} ${t('minutes')}` : t('Not timed')}</td>
                          <td>{session.learnerScoreBefore != null || session.learnerScoreAfter != null ? `${session.learnerScoreBefore ?? t('Not set')} -> ${session.learnerScoreAfter ?? t('Not set')}` : t('Not scored')}</td>
                          <td>{session.aiSummary || t('No AI summary')}</td>
                          <td>{session.createdAt ? formatDate(session.createdAt) : t('Not dated')}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            ) : null}
          </div>
        ) : null}
      </DetailDrawer>

      <FormModal
        title={t('New assignment')}
        description={t('Connect one learner to either an AI mentor or a human mentor.')}
        isOpen={assignmentFormOpen}
        onClose={() => setAssignmentFormOpen(false)}
        size="lg"
        footer={(
          <>
            <button type="button" className="btn btn-ghost" onClick={() => setAssignmentFormOpen(false)} disabled={Boolean(busyId)}>{t('Cancel')}</button>
            <button type="button" className="btn btn-primary" onClick={saveAssignmentForm} disabled={Boolean(busyId)}>{t('Create assignment')}</button>
          </>
        )}
      >
        <ErrorBanner message={formError} />
        <div className="admin-form-grid">
          <label>{t('Assignment type')}
            <select className="input" value={assignmentForm.assignmentType} onChange={(event) => setAssignmentForm({ ...assignmentForm, assignmentType: event.target.value as MentorAssignment['assignmentType'], aiMentorId: '', humanMentorWorkerProfileId: '' })}>
              <option value="ai_mentor">{t('AI mentor')}</option>
              <option value="human_mentor">{t('Human mentor')}</option>
            </select>
          </label>
          <label>{t('Status')}
            <select className="input" value={assignmentForm.status} onChange={(event) => setAssignmentForm({ ...assignmentForm, status: event.target.value as MentorAssignment['status'] })}>
              {['assigned', 'active', 'paused', 'completed', 'cancelled'].map((status) => <option key={status} value={status}>{t(status)}</option>)}
            </select>
          </label>
          {learnerProfileOptions.length ? (
            <label>{t('Learner profile')}
              <select className="input" value={assignmentForm.learnerProfileId} onChange={(event) => setAssignmentForm({ ...assignmentForm, learnerProfileId: event.target.value, learningGoalId: '' })}>
                <option value="">{t('Choose learner')}</option>
                {learnerProfileOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
              </select>
            </label>
          ) : (
            <label>{t('Learner profile id')}<input className="input" value={assignmentForm.learnerProfileId} onChange={(event) => setAssignmentForm({ ...assignmentForm, learnerProfileId: event.target.value })} /></label>
          )}
          {learnerOptions.length ? (
            <label>{t('Learner user account')}
              <select className="input" value={assignmentForm.learnerUserId} onChange={(event) => setAssignmentForm({ ...assignmentForm, learnerUserId: event.target.value, learningGoalId: '' })}>
                <option value="">{t('Optional user account')}</option>
                {learnerOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
              </select>
            </label>
          ) : null}
          {assignmentForm.assignmentType === 'ai_mentor' ? (
            <label>{t('AI mentor')}
              <select className="input" value={assignmentForm.aiMentorId} onChange={(event) => setAssignmentForm({ ...assignmentForm, aiMentorId: event.target.value, skillTrack: aiMentors.find((mentor) => mentor.id === event.target.value)?.skillTrack || assignmentForm.skillTrack })}>
                <option value="">{t('Choose AI mentor')}</option>
                {aiMentors.map((mentor) => <option key={mentor.id} value={mentor.id}>{mentor.name}</option>)}
              </select>
            </label>
          ) : (
            humanMentorOptions.length ? (
              <label>{t('Human mentor')}
                <select className="input" value={assignmentForm.humanMentorWorkerProfileId} onChange={(event) => setAssignmentForm({ ...assignmentForm, humanMentorWorkerProfileId: event.target.value })}>
                  <option value="">{t('Choose human mentor')}</option>
                  {humanMentorOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                </select>
              </label>
            ) : (
              <label>{t('Human mentor worker profile id')}<input className="input" value={assignmentForm.humanMentorWorkerProfileId} onChange={(event) => setAssignmentForm({ ...assignmentForm, humanMentorWorkerProfileId: event.target.value })} /></label>
            )
          )}
          {learningGoalOptions.length ? (
            <label className="admin-form-wide">{t('Learning goal')}
              <select
                className="input"
                value={assignmentForm.learningGoalId}
                onChange={(event) => {
                  const goal = learningGoalOptions.find((option) => option.value === event.target.value)
                  setAssignmentForm({
                    ...assignmentForm,
                    learningGoalId: event.target.value,
                    learnerProfileId: goal?.learnerProfileId || assignmentForm.learnerProfileId,
                    learnerUserId: goal?.learnerUserId || assignmentForm.learnerUserId,
                    skillId: goal?.skillId || assignmentForm.skillId,
                    skillTrack: assignmentForm.skillTrack || goal?.skillName || '',
                  })
                }}
              >
                <option value="">{t('Optional learning goal')}</option>
                {learningGoalOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
              </select>
            </label>
          ) : null}
          <label>{t('Skill track')}<input className="input" value={assignmentForm.skillTrack} onChange={(event) => setAssignmentForm({ ...assignmentForm, skillTrack: event.target.value })} /></label>
          <label>{t('Target completion')}<input className="input" type="datetime-local" value={assignmentForm.targetCompletionAt} onChange={(event) => setAssignmentForm({ ...assignmentForm, targetCompletionAt: event.target.value })} /></label>
          <label className="admin-form-wide">{t('Progress summary')}<textarea className="input" rows={3} value={assignmentForm.progressSummary} onChange={(event) => setAssignmentForm({ ...assignmentForm, progressSummary: event.target.value })} /></label>
        </div>
      </FormModal>

      <FormModal
        title={t('Generate learning path')}
        description={t('Use the server-side LLM provider to create a roadmap and optionally save it to the Mentor Admin learning path ledger.')}
        isOpen={learningPathFormOpen}
        onClose={() => setLearningPathFormOpen(false)}
        size="lg"
        footer={(
          <>
            <button type="button" className="btn btn-ghost" onClick={() => setLearningPathFormOpen(false)} disabled={Boolean(busyId)}>{t('Cancel')}</button>
            <button type="button" className="btn btn-primary" onClick={generateLearningPath} disabled={Boolean(busyId)}>{t('Generate roadmap')}</button>
          </>
        )}
      >
        <ErrorBanner message={formError} />
        <div className="admin-form-grid">
          <label>{t('AI mentor')}
            <select className="input" value={learningPathForm.aiMentorId} onChange={(event) => setLearningPathForm({ ...learningPathForm, aiMentorId: event.target.value, skillTrack: aiMentors.find((mentor) => mentor.id === event.target.value)?.skillTrack || learningPathForm.skillTrack })}>
              <option value="">{t('No AI mentor selected')}</option>
              {aiMentors.map((mentor) => <option key={mentor.id} value={mentor.id}>{mentor.name}</option>)}
            </select>
          </label>
          <label>{t('Skill track')}<input className="input" value={learningPathForm.skillTrack} onChange={(event) => setLearningPathForm({ ...learningPathForm, skillTrack: event.target.value })} /></label>
          <label>{t('Learner current level')}<input className="input" value={learningPathForm.learnerCurrentLevel} onChange={(event) => setLearningPathForm({ ...learningPathForm, learnerCurrentLevel: event.target.value })} /></label>
          <label>{t('Timeline')}<input className="input" value={learningPathForm.timeline} onChange={(event) => setLearningPathForm({ ...learningPathForm, timeline: event.target.value })} /></label>
          {learnerProfileOptions.length ? (
            <label>{t('Learner profile')}
              <select className="input" value={learningPathForm.learnerProfileId} onChange={(event) => setLearningPathForm({ ...learningPathForm, learnerProfileId: event.target.value })}>
                <option value="">{t('Choose learner')}</option>
                {learnerProfileOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
              </select>
            </label>
          ) : (
            <label>{t('Learner profile id')}<input className="input" value={learningPathForm.learnerProfileId} onChange={(event) => setLearningPathForm({ ...learningPathForm, learnerProfileId: event.target.value })} /></label>
          )}
          {learnerOptions.length ? (
            <label>{t('Learner user account')}
              <select className="input" value={learningPathForm.learnerUserId} onChange={(event) => setLearningPathForm({ ...learningPathForm, learnerUserId: event.target.value })}>
                <option value="">{t('Optional user account')}</option>
                {learnerOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
              </select>
            </label>
          ) : null}
          {assignmentOptions.length ? (
            <label>{t('Assignment')}
              <select
                className="input"
                value={learningPathForm.assignmentId}
                onChange={(event) => {
                  const assignment = assignmentOptions.find((option) => option.value === event.target.value)
                  setLearningPathForm({
                    ...learningPathForm,
                    assignmentId: event.target.value,
                    aiMentorId: assignment?.aiMentorId || learningPathForm.aiMentorId,
                    learnerUserId: assignment?.learnerUserId || learningPathForm.learnerUserId,
                    learnerProfileId: assignment?.learnerProfileId || learningPathForm.learnerProfileId,
                    skillTrack: assignment?.skillTrack || learningPathForm.skillTrack,
                  })
                }}
              >
                <option value="">{t('No assignment selected')}</option>
                {assignmentOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
              </select>
            </label>
          ) : (
            <label>{t('Assignment id')}<input className="input" value={learningPathForm.assignmentId} onChange={(event) => setLearningPathForm({ ...learningPathForm, assignmentId: event.target.value })} /></label>
          )}
          <label>{t('Status')}
            <select className="input" value={learningPathForm.status} onChange={(event) => setLearningPathForm({ ...learningPathForm, status: event.target.value as MentorLearningPath['status'] })}>
              {['draft', 'active', 'completed', 'archived'].map((status) => <option key={status} value={status}>{t(status)}</option>)}
            </select>
          </label>
          <label className="admin-form-wide">{t('Learner goal')}<textarea className="input" rows={3} value={learningPathForm.learnerGoal} onChange={(event) => setLearningPathForm({ ...learningPathForm, learnerGoal: event.target.value })} /></label>
          <label className="admin-form-wide helper">
            <input type="checkbox" checked={learningPathForm.saveLearningPath} onChange={(event) => setLearningPathForm({ ...learningPathForm, saveLearningPath: event.target.checked })} />
            {t('Save generated roadmap as a learning path')}
          </label>
        </div>
        {learningPathPreview ? (
          <pre className="admin-code-block">{typeof learningPathPreview === 'string' ? learningPathPreview : JSON.stringify(learningPathPreview, null, 2)}</pre>
        ) : null}
      </FormModal>

      <FormModal
        title={aiActionTitle}
        description={t('Run an AI mentor action through the server-side LLM service. Results are recorded in Mentor Admin AI usage.')}
        isOpen={aiActionFormOpen}
        onClose={() => setAiActionFormOpen(false)}
        size="lg"
        footer={(
          <>
            <button type="button" className="btn btn-ghost" onClick={() => setAiActionFormOpen(false)} disabled={Boolean(busyId)}>{t('Close')}</button>
            <button type="button" className="btn btn-primary" onClick={runAiAction} disabled={Boolean(busyId)}>{t('Run action')}</button>
          </>
        )}
      >
        <ErrorBanner message={formError} />
        <div className="admin-form-grid">
          <label>{t('AI mentor')}
            <select className="input" value={aiActionForm.aiMentorId} onChange={(event) => setAiActionForm({ ...aiActionForm, aiMentorId: event.target.value, skillTrack: aiMentors.find((mentor) => mentor.id === event.target.value)?.skillTrack || aiActionForm.skillTrack })}>
              <option value="">{t('No AI mentor selected')}</option>
              {aiMentors.map((mentor) => <option key={mentor.id} value={mentor.id}>{mentor.name}</option>)}
            </select>
          </label>
          {aiActionMode !== 'progress_summary' && aiActionMode !== 'french_progress' ? (
            <label>{t('Skill track')}<input className="input" value={aiActionForm.skillTrack} onChange={(event) => setAiActionForm({ ...aiActionForm, skillTrack: event.target.value })} /></label>
          ) : null}
          {learnerProfileOptions.length ? (
            <label>{t('Learner profile')}
              <select className="input" value={aiActionForm.learnerProfileId} onChange={(event) => setAiActionForm({ ...aiActionForm, learnerProfileId: event.target.value })}>
                <option value="">{t('Choose learner')}</option>
                {learnerProfileOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
              </select>
            </label>
          ) : (
            <label>{t('Learner profile id')}<input className="input" value={aiActionForm.learnerProfileId} onChange={(event) => setAiActionForm({ ...aiActionForm, learnerProfileId: event.target.value })} /></label>
          )}
          {learnerOptions.length ? (
            <label>{t('Learner user account')}
              <select className="input" value={aiActionForm.learnerUserId} onChange={(event) => setAiActionForm({ ...aiActionForm, learnerUserId: event.target.value })}>
                <option value="">{t('Optional user account')}</option>
                {learnerOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
              </select>
            </label>
          ) : null}
          {assignmentOptions.length ? (
            <label>{t('Assignment')}
              <select className="input" value={aiActionForm.assignmentId} onChange={(event) => applyAssignmentToAiAction(event.target.value)}>
                <option value="">{t('No assignment selected')}</option>
                {assignmentOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
              </select>
            </label>
          ) : (
            <label>{t('Assignment id')}<input className="input" value={aiActionForm.assignmentId} onChange={(event) => setAiActionForm({ ...aiActionForm, assignmentId: event.target.value })} /></label>
          )}
          {aiActionMode.startsWith('french_') ? (
            <>
              <label>{t('Language preference')}
                <select className="input" value={aiActionForm.languagePreference} onChange={(event) => setAiActionForm({ ...aiActionForm, languagePreference: event.target.value })}>
                  <option value="fr">{t('French')}</option>
                  <option value="en">{t('Simple English')}</option>
                </select>
              </label>
              <label>{t('Learner level')}<input className="input" value={aiActionForm.level} onChange={(event) => setAiActionForm({ ...aiActionForm, level: event.target.value })} /></label>
            </>
          ) : null}
          {aiActionMode === 'lesson' || aiActionMode === 'quiz' ? (
            <>
              <label>{t('Module')}<input className="input" value={aiActionForm.module} onChange={(event) => setAiActionForm({ ...aiActionForm, module: event.target.value })} /></label>
              <label>{t('Level')}<input className="input" value={aiActionForm.level} onChange={(event) => setAiActionForm({ ...aiActionForm, level: event.target.value })} /></label>
            </>
          ) : null}
          {aiActionMode === 'french_reading' ? (
            <>
              <label className="admin-form-wide">{t('Passage')}<textarea className="input" rows={4} value={aiActionForm.passage} onChange={(event) => setAiActionForm({ ...aiActionForm, passage: event.target.value })} /></label>
              <label className="admin-form-wide">{t('Generated passage request')}<textarea className="input" rows={3} value={aiActionForm.generatedPassageRequest} onChange={(event) => setAiActionForm({ ...aiActionForm, generatedPassageRequest: event.target.value })} /></label>
            </>
          ) : null}
          {aiActionMode === 'french_writing' ? (
            <>
              <label className="admin-form-wide">{t('Student paragraph')}<textarea className="input" rows={4} value={aiActionForm.studentParagraph} onChange={(event) => setAiActionForm({ ...aiActionForm, studentParagraph: event.target.value })} /></label>
              <label className="admin-form-wide">{t('Assignment goal')}<textarea className="input" rows={2} value={aiActionForm.assignmentGoal} onChange={(event) => setAiActionForm({ ...aiActionForm, assignmentGoal: event.target.value })} /></label>
            </>
          ) : null}
          {aiActionMode === 'french_dictee' ? (
            <label className="admin-form-wide">{t('Theme')}<input className="input" value={aiActionForm.theme} onChange={(event) => setAiActionForm({ ...aiActionForm, theme: event.target.value })} /></label>
          ) : null}
          {aiActionMode === 'roleplay' ? (
            <>
              <label className="admin-form-wide">{t('Scenario')}<textarea className="input" rows={3} value={aiActionForm.scenario} onChange={(event) => setAiActionForm({ ...aiActionForm, scenario: event.target.value })} /></label>
              <label className="admin-form-wide">{t('Learner message')}<textarea className="input" rows={3} value={aiActionForm.learnerMessage} onChange={(event) => setAiActionForm({ ...aiActionForm, learnerMessage: event.target.value })} /></label>
              <label className="admin-form-wide">{t('Conversation summary')}<textarea className="input" rows={3} value={aiActionForm.conversationSummary} onChange={(event) => setAiActionForm({ ...aiActionForm, conversationSummary: event.target.value })} /></label>
            </>
          ) : null}
          {aiActionMode === 'progress_summary' ? (
            <>
              <label className="admin-form-wide">{t('Learner activity')}<textarea className="input" rows={4} value={aiActionForm.learnerActivity} onChange={(event) => setAiActionForm({ ...aiActionForm, learnerActivity: event.target.value })} /></label>
              <label>{t('Quiz score')}<input className="input" value={aiActionForm.quizScore} onChange={(event) => setAiActionForm({ ...aiActionForm, quizScore: event.target.value })} /></label>
              <label>{t('Completed modules')}<input className="input" value={aiActionForm.completedModules} onChange={(event) => setAiActionForm({ ...aiActionForm, completedModules: event.target.value })} /></label>
            </>
          ) : null}
          {aiActionMode === 'french_progress' ? (
            <>
              <label className="admin-form-wide">{t('Learner activity')}<textarea className="input" rows={4} value={aiActionForm.learnerActivity} onChange={(event) => setAiActionForm({ ...aiActionForm, learnerActivity: event.target.value })} /></label>
              <label>{t('Completed exercises')}<input className="input" value={aiActionForm.completedExercises} onChange={(event) => setAiActionForm({ ...aiActionForm, completedExercises: event.target.value })} /></label>
              <label>{t('Quiz/checkpoint scores')}<input className="input" value={aiActionForm.quizScores} onChange={(event) => setAiActionForm({ ...aiActionForm, quizScores: event.target.value })} /></label>
              <label className="admin-form-wide">{t('Recurring mistakes')}<textarea className="input" rows={3} value={aiActionForm.recurringMistakes} onChange={(event) => setAiActionForm({ ...aiActionForm, recurringMistakes: event.target.value })} /></label>
            </>
          ) : null}
        </div>
        {aiActionPreview ? (
          <div className="stack">
            <pre className="admin-code-block">{typeof aiActionPreview === 'string' ? aiActionPreview : JSON.stringify(aiActionPreview, null, 2)}</pre>
            {frenchActivityTypeByMode[aiActionMode] ? (
              <div className="button-row">
                <button type="button" className="btn btn-secondary" onClick={saveAiActionPreviewAsActivity} disabled={Boolean(busyId) || Boolean(aiActionActivitySaved)}>{t('Save as activity evidence')}</button>
                {aiActionActivitySaved ? <span className="status-pill status-pill--approved">{t('Saved as activity evidence')}</span> : null}
              </div>
            ) : null}
          </div>
        ) : null}
      </FormModal>

      <FormModal
        title={t('New skill')}
        description={t('Create a mentorship skill track that can be used by learners, human mentors, and AI mentors.')}
        isOpen={skillFormOpen}
        onClose={() => setSkillFormOpen(false)}
        footer={(
          <>
            <button type="button" className="btn btn-ghost" onClick={() => setSkillFormOpen(false)} disabled={Boolean(busyId)}>{t('Cancel')}</button>
            <button type="button" className="btn btn-primary" onClick={saveSkillForm} disabled={Boolean(busyId)}>{t('Create skill')}</button>
          </>
        )}
      >
        <ErrorBanner message={formError} />
        <div className="admin-form-grid">
          <label>{t('Name')}<input className="input" value={skillForm.name} onChange={(event) => setSkillForm({ ...skillForm, name: event.target.value })} /></label>
          <label>{t('Category')}<input className="input" value={skillForm.category} onChange={(event) => setSkillForm({ ...skillForm, category: event.target.value })} /></label>
          <label>{t('Status')}
            <select className="input" value={skillForm.status} onChange={(event) => setSkillForm({ ...skillForm, status: event.target.value as NonNullable<MentorAdminSkill['status']> })}>
              {['active', 'inactive', 'archived'].map((status) => <option key={status} value={status}>{t(status)}</option>)}
            </select>
          </label>
          <label className="admin-form-wide">{t('Description')}<textarea className="input" rows={3} value={skillForm.description} onChange={(event) => setSkillForm({ ...skillForm, description: event.target.value })} /></label>
        </div>
      </FormModal>
    </section>
  )
}
